import React, { useState } from "react";
import {
    Box,
    Button,
    Container,
    Grid,
    TextField,
    Typography,
    MenuItem,
    IconButton,
    Breadcrumbs,
    Link,
    Divider,
    Autocomplete,
    useTheme,
    ToggleButton,
    ToggleButtonGroup
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Form, ErrorMessage, getIn } from "formik";
import * as Yup from "yup";
import CompulsoryFieldsLabel from "../../helpers/CompulsoryFields";
import queryGraphql from "../../helpers/ApiInterface";
import { useAuth } from "../../helpers/AuthProvider";
import MiniBot from "../MiniBot";
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../MainDrawer";

// --- Cloud Provider Configuration ---

const gcpclusterTypeList = ["Autopilot", "Standard"];
const awsclusterTypeList = ["Auto Mode", "Managed Node", "Fargate"];
const azureClusterTypes = ["Autopilot", "Standard"];
const azurePricingTiers = ["Free", "Standard"];

// Data for the new provider toggle buttons
const providerDisplayData = [
    { value: 'gcp', label: 'Google Cloud Platform', icon: '/assets/gcp_mini_icon.svg' },
    { value: 'aws', label: 'Amazon Web Services', icon: '/assets/aws_mini_icon.svg' },
    { value: 'azure', label: 'Microsoft Azure', icon: '/assets/azure_mini_icon.svg' }
];

// --- Region Lists ---

const gcpregionList = [ "africa-south1", "asia-east1", "asia-east2", "asia-northeast1", "asia-northeast2", "asia-northeast3", "asia-south1", "asia-south2", "asia-southeast1", "asia-southeast2", "australia-southeast1", "australia-southeast2", "europe-central2", "europe-north1", "europe-southwest1", "europe-west1", "europe-west10", "europe-west12", "europe-west2", "europe-west3", "europe-west4", "europe-west6", "europe-west8", "europe-west9", "me-central1", "me-central2", "me-west1", "northamerica-northeast1", "northamerica-northeast2", "southamerica-east1", "southamerica-west1", "us-central1", "us-east1", "us-east4", "us-east5", "us-south1", "us-west1", "us-west2", "us-west3", "us-west4"];
const awsregionList = [ "us-east-2", "us-east-1", "us-west-1", "us-west-2", "af-south-1", "ap-east-1", "ap-south-2", "ap-southeast-3", "ap-southeast-5", "ap-southeast-4", "ap-south-1", "ap-northeast-3", "ap-northeast-2", "ap-southeast-1", "ap-southeast-2", "ap-southeast-7", "ap-northeast-1", "ca-central-1", "ca-west-1", "eu-central-1", "eu-west-1", "eu-west-2", "eu-south-1", "eu-west-3", "eu-south-2", "eu-north-1", "eu-central-2", "il-central-1", "mx-central-1", "me-south-1", "me-central-1", "sa-east-1", "us-gov-east-1", "us-gov-west-1"];
const azureRegionList = ["eastus", "eastus2", "westus", "westus2", "centralus","centralindia", "northcentralus", "southcentralus", "westcentralus", "canadacentral", "canadaeast", "brazilsouth", "northeurope", "westeurope", "uksouth", "ukwest", "francecentral", "germanywestcentral", "switzerlandnorth", "uaenorth", "japaneast", "japanwest", "koreacentral", "koreasouth", "southeastasia", "australiaeast"];


// --- Validation Schema ---

const validationSchema = Yup.object().shape({
    cloudProvider: Yup.string().required("This field is required").oneOf(["gcp", "aws", "azure"], "Invalid cloud provider"),
    clusterName: Yup.string().required("This field is required"),
    gcpProjectId: Yup.string().required("This field is required"),
    region: Yup.string().required("This field is required").matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric").strict(),
    gcpClusterViewers: Yup.array().of(Yup.string().required("This field is required").matches(/^[a-zA-Z0-9._%+-]+@searce\.com$/, "Invalid domain").strict()).required("This field is required").min(1, "This field is required").strict(),
    applicationRequesters: Yup.array().of(Yup.string().required("This field is required").matches(/^[a-zA-Z0-9._%+-]+@searce\.com$/, "Invalid domain").strict()).required("This field is required").min(1, "This field is required").strict(),
    costLabelPairs: Yup.array().of(Yup.object({
        cost: Yup.string().required("This field is required").strict().lowercase("This field must be in lowercase").matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric").max(20, "This field must not exceed 20 characters"),
        label: Yup.string().required("This field is required").strict().lowercase("This field must be in lowercase").matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric").max(20, "This field must not exceed 20 characters"),
    })).required("This field is required"),
    environment: Yup.string().required("This field is required").oneOf(["dev", "staging", "prod"]),
    clusterSize: Yup.string().required("This field is required").oneOf(["small", "medium", "large"]),
    clusterType: Yup.string().required("This field is required"),
    departmentValue: Yup.string().required("This field is required").strict().lowercase("This field must be in lowercase").matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric").max(30, "This field must not exceed 30 characters"),
    costCenterValue: Yup.string().required("This field is required").strict().lowercase("This field must be in lowercase").matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric").max(20, "This field must not exceed 20 characters"),
    resourceGroup: Yup.string().when("cloudProvider", { is: 'azure', then: () => Yup.string().required("Resource Group is required for Azure") }),
    aksPricingTier: Yup.string().when("cloudProvider", { is: 'azure', then: () => Yup.string().required("Pricing Tier is required for Azure") }),
});

interface ClusterFormProps { isEdit: boolean; }
interface ClusterFormContentProps { isEdit: boolean; }
interface oneCluster { id: string; clusterName: string; environment: string; status: string; }

const ClusterFormContent: React.FC<ClusterFormContentProps> = ({ isEdit }) => {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const { user } = useAuth();
    const isViewer = user.roles?.some((role: string) => role === "viewer") || false;
    const [cluster, setCluster] = useState<any>({});
    const [clusters, setClusters] = useState<oneCluster[]>([]);
    const { id } = useParams();
    const [devDisable, setDevDisable] = useState<boolean>(false);
    const [stageDisable, setStageDisable] = useState<boolean>(false);
    const [prodDisable, setProdDisable] = useState<boolean>(false);
    const [ClusterName, setClusterName] = useState<string>();

    React.useEffect(() => {
        const fetchData = async () => {
            if (!isEdit || !id) return;
            try {
                const data = await queryGraphql(`query($clusterId: String!){ cluster(id:$clusterId) { id, clusterName, cloudProvider, clusterOwner{ id, name, googleProfilePicture }, gcpProjectId, environment, region, clusterSize, clusterType, gcpClusterViewers, applicationRequesters, costLabelPairs{ cost, label }, status, createdAt, resourceGroup, aksPricingTier } }`, { clusterId: id });
                if (data.cluster) setCluster(data.cluster);
            } catch (error) { console.log(error); }
        };
        fetchData();
    }, [id, isEdit]);

    React.useEffect(() => {
        const fetchAllClusters = async () => {
            try {
                const data = await queryGraphql(`query Clusters { clusters { id clusterName environment status } }`);
                if (data.clusters) {
                    setClusters(data.clusters.filter((e: any) => e.status !== "rejected"));
                }
            } catch (error) { console.log(error); }
        };
        fetchAllClusters();
    }, []);

    // **FIXED**: Implemented the new environment progression logic.
    React.useEffect(() => {
        const existingEnvs = clusters
            .filter(item => item.clusterName.toLowerCase() === (ClusterName || "").toLowerCase())
            .map(item => item.environment);

        const devExists = existingEnvs.includes("dev");
        const stageExists = existingEnvs.includes("staging");
        const prodExists = existingEnvs.includes("prod");

        // If prod exists, disable all. This is the terminal state.
        if (prodExists) {
            setDevDisable(true);
            setStageDisable(true);
            setProdDisable(true);
            return;
        }

        // If dev and staging exist, only prod is available.
        if (devExists && stageExists) {
            setDevDisable(true);
            setStageDisable(true);
            setProdDisable(false);
            return;
        }
        
        // If only dev exists, staging and prod are available.
        if (devExists) {
            setDevDisable(true);
            setStageDisable(false);
            setProdDisable(false);
            return;
        }

        // If no clusters exist for this name, only dev is available.
        setDevDisable(false);
        setStageDisable(true);
        setProdDisable(true);

    }, [ClusterName, clusters]);

    React.useEffect(() => {
        const fetchUsers = async () => {
            try {
                const data = await queryGraphql(`query{ users { id, email } }`);
                if (data.users) setUsers(data.users);
            } catch (error) { console.log(error); }
        };
        fetchUsers();
    }, []);

    return (
        <Container disableGutters maxWidth={false} sx={{ padding: "24px", bgcolor: "#fafafa", minHeight: "100vh" }}>
            <MiniBot />
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
                    <Link underline="hover" color="inherit" href="/clusters">Clusters</Link>
                    <Typography color="text.primary">{isEdit ? "Edit Cluster Details" : "Add New Cluster"}</Typography>
                </Breadcrumbs>
                <Box display="flex" alignItems="center" mt={2}>
                    <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/clusters")}><ArrowBackIosNewIcon fontSize="small" /></IconButton>
                    <Typography fontFamily="Gilroy-Bold" fontSize="18px">{isEdit ? "Edit Cluster Details" : "Add New Cluster"}</Typography>
                </Box>
            </Box>
            <Grid container>
                <Grid item xs={1}></Grid>
                <Grid item xs={10}>
                    {((isEdit && cluster.id) || !isEdit) && (
                        <Formik
                            initialValues={{
                                cloudProvider: isEdit ? (cluster.cloudProvider || "gcp") : "gcp",
                                clusterName: isEdit ? cluster.clusterName : "",
                                gcpProjectId: isEdit ? cluster.gcpProjectId : "",
                                region: isEdit ? cluster.region : "",
                                gcpClusterViewers: isEdit ? cluster.gcpClusterViewers : [],
                                applicationRequesters: isEdit ? cluster.applicationRequesters : [],
                                costLabelPairs: isEdit ? cluster.costLabelPairs?.filter((e: any) => !["department", "cost-center", "cluster-owner"].includes(e.cost)) || [] : [],
                                environment: isEdit ? cluster.environment : "",
                                clusterSize: isEdit ? cluster.clusterSize : "",
                                clusterType: isEdit ? cluster.clusterType : "",
                                departmentValue: isEdit ? cluster.costLabelPairs?.find((elem: any) => elem.cost === "department")?.label || "" : "",
                                costCenterValue: isEdit ? cluster.costLabelPairs?.find((elem: any) => elem.cost === "cost-center")?.label || "" : "",
                                resourceGroup: isEdit ? cluster.resourceGroup : "",
                                aksPricingTier: isEdit ? cluster.aksPricingTier : "",
                            }}
                            validationSchema={validationSchema}
                            onSubmit={async (values) => {
                                let keyValuePairs = [];
                                keyValuePairs.push(
                                    { cost: "cluster-owner", label: (user.name as string).toLowerCase().replace(" ", "-") },
                                    { cost: "department", label: values.departmentValue },
                                    { cost: "cost-center", label: values.costCenterValue },
                                    ...values.costLabelPairs.map((e: any) => ({ cost: e.cost, label: e.label }))
                                );
                                const inputPayload: any = {
                                    cloudProvider: values.cloudProvider,
                                    clusterName: values.clusterName,
                                    gcpProjectId: values.gcpProjectId,
                                    region: values.region,
                                    clusterType: values.clusterType,
                                    environment: values.environment,
                                    clusterSize: values.clusterSize,
                                    gcpClusterViewers: values.gcpClusterViewers,
                                    applicationRequesters: values.applicationRequesters,
                                    costLabelPairs: keyValuePairs,
                                };
                                if (values.cloudProvider === 'azure') {
                                    inputPayload.resourceGroup = values.resourceGroup;
                                    inputPayload.aksPricingTier = values.aksPricingTier;
                                }
                                if (!isEdit) {
                                    const data = await queryGraphql(`mutation CreateCluster($input: ClusterCreateInput) { createCluster(input: $input) { id } }`, { input: inputPayload });
                                    if (data.createCluster.id) {
                                        await queryGraphql(`mutation Mutation($input: ResourceStatusCreateInput) { createResourceStatus(input: $input) { id } }`, { input: { currentStatus: "pending", resourceEnvironment: values.environment, resourceId: data.createCluster.id, resourceName: values.clusterName, resourceType: "cluster" } });
                                        navigate("/clusters");
                                    }
                                } else {
                                    const data = await queryGraphql(`mutation UpdateCluster($input: ClusterUpdateInput) { updateCluster(input: $input) { id } }`, { input: { id: id, ...inputPayload } });
                                    if (data.updateCluster.id) {
                                        await queryGraphql(`mutation Mutation($input: ResourceStatusUpdateInput) { updateResourceStatus(input: $input) { id } }`, { input: { currentStatus: "pending", resourceId: id, resourceName: values.clusterName } });
                                        navigate("/clusters");
                                    }
                                }
                            }}
                        >
                            {({ handleChange, handleBlur, values, setFieldValue, errors, touched, isValid, dirty }) => (
                                <Form>
                                    <Box>
                                        <Typography fontFamily="Gilroy-Medium" fontSize="16px" mb={4}>
                                            {isEdit ? "Make changes to below details and save" : "Fill the below details to add cluster"}
                                        </Typography>
                                        
                                        <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
                                            <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}>Cloud Provider Details</Typography>
                                            <Divider sx={{ marginBottom: "24px" }} />
                                            <Grid container spacing={4} style={{ marginBottom: "16px" }}>
                                                <Grid item xs={12}>
                                                    <CompulsoryFieldsLabel fieldName="Cloud Provider" />
                                                    <ToggleButtonGroup value={values.cloudProvider} exclusive
                                                        onChange={(event, newProvider) => {
                                                            if (newProvider !== null) {
                                                                setFieldValue('cloudProvider', newProvider);
                                                                setFieldValue("region", "");
                                                                setFieldValue("clusterType", "");
                                                                setFieldValue("clusterSize", "");
                                                                setFieldValue("environment", "");
                                                            }
                                                        }}
                                                        aria-label="cloud provider"
                                                        sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}
                                                    >
                                                        {providerDisplayData.map((p) => (
                                                            <ToggleButton key={p.value} value={p.value} aria-label={p.label}
                                                                sx={{ display: 'flex', gap: 1, alignItems: 'center', textTransform: 'none', fontFamily: 'Gilroy-Medium', borderRadius: '24px !important', border: '1px solid #ccc !important', px: 2.5, py: 1, '&.Mui-selected': { borderColor: '#000 !important', backgroundColor: 'rgba(0, 0, 0, 0.08)' }, '&.Mui-selected:hover': { backgroundColor: 'rgba(0, 0, 0, 0.12)' } }}
                                                            >
                                                                <img src={p.icon} alt={`${p.label} icon`} style={{ width: '20px', height: '20px' }} />
                                                                {p.label}
                                                            </ToggleButton>
                                                        ))}
                                                    </ToggleButtonGroup>
                                                    <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="cloudProvider" component="div" /></div>
                                                </Grid>
                                            </Grid>
                                        </div>

                                        <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
                                            <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}>Basic Information</Typography>
                                            <Divider sx={{ marginBottom: "24px" }} />
                                            <Grid container spacing={4} style={{ marginBottom: "16px" }}>
                                                <Grid item xs={12} sm={6}>
                                                    <CompulsoryFieldsLabel fieldName="Cluster Name" />
                                                    <TextField sx={{ "& .MuiFilledInput-root": { border: errors["clusterName"] && touched["clusterName"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }} variant="filled" fullWidth name="clusterName" value={values.clusterName} onChange={(e) => { handleChange(e); setClusterName(e.target.value); }} onBlur={handleBlur} placeholder="Enter name here" size="medium" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                                    <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="clusterName" component="div" /></div>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <CompulsoryFieldsLabel fieldName={values.cloudProvider === 'aws' ? "AWS Account ID" : values.cloudProvider === 'azure' ? "Subscription ID" : "GCP Project ID"} />
                                                    <TextField variant="filled" fullWidth name="gcpProjectId" value={values.gcpProjectId} onChange={handleChange} onBlur={handleBlur} placeholder={values.cloudProvider === 'aws' ? "Enter AWS Account ID" : values.cloudProvider === 'azure' ? "Enter Subscription ID" : "Enter GCP Project ID"} size="medium" sx={{ "& .MuiFilledInput-root": { border: errors["gcpProjectId"] && touched["gcpProjectId"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }} hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                                    <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="gcpProjectId" component="div" /></div>
                                                </Grid>
                                                {values.cloudProvider === 'azure' && (
                                                    <Grid item xs={12} sm={6}>
                                                        <CompulsoryFieldsLabel fieldName="Resource Group" />
                                                        <TextField name="resourceGroup" value={values.resourceGroup} onChange={handleChange} onBlur={handleBlur} placeholder="Enter Resource Group" variant="filled" fullWidth hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                                        <div style={{ color: "red", marginTop: "4px" }}><ErrorMessage name="resourceGroup" /></div>
                                                    </Grid>
                                                )}
                                                <Grid item xs={12} sm={6}>
                                                    <CompulsoryFieldsLabel fieldName="Cluster Type" />
                                                    <TextField select placeholder="Select" name="clusterType" value={values.clusterType} onChange={handleChange} onBlur={handleBlur} variant="filled" fullWidth hiddenLabel size="medium" sx={{ "& .MuiFilledInput-root": { border: errors["clusterType"] && touched["clusterType"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
                                                        {(values.cloudProvider === 'gcp' ? gcpclusterTypeList : values.cloudProvider === 'aws' ? awsclusterTypeList : azureClusterTypes).map((e) => (<MenuItem key={e} value={e}>{e}</MenuItem>))}
                                                    </TextField>
                                                    <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="clusterType" component="div" /></div>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <CompulsoryFieldsLabel fieldName="Region" />
                                                    <Autocomplete options={values.cloudProvider === 'gcp' ? gcpregionList : values.cloudProvider === 'aws' ? awsregionList : azureRegionList} filterSelectedOptions value={values.region || null} onChange={(e, newVal) => setFieldValue("region", newVal || "")} renderInput={(params) => (<TextField {...params} variant="filled" fullWidth placeholder="Select" size="medium" hiddenLabel sx={{ "& .MuiFilledInput-root": { border: errors["region"] && touched["region"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }} InputProps={{ ...params.InputProps, style: { borderRadius: "8px" }, disableUnderline: true }} />)} />
                                                    <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>{getIn(errors, `region`)}</div>
                                                </Grid>
                                                {values.cloudProvider === 'azure' && (
                                                    <Grid item xs={12} sm={6}>
                                                        <CompulsoryFieldsLabel fieldName="AKS pricing tier" />
                                                        <TextField select fullWidth variant="filled" hiddenLabel name="aksPricingTier" value={values.aksPricingTier} onChange={handleChange} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
                                                            {azurePricingTiers.map(option => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                                                        </TextField>
                                                        <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="aksPricingTier" /></div>
                                                    </Grid>
                                                )}
                                                <Grid item xs={12} sm={6}>
                                                    <CompulsoryFieldsLabel fieldName="Environment" />
                                                    <Box display="flex" gap={2} mt="14px">
                                                        <Button sx={{ borderRadius: 5, borderColor: values.environment === "dev" ? "#000000" : "#666666", color: values.environment === "dev" ? "#000000" : "#666666", bgcolor: values.environment === "dev" ? "#E0E0E0" : "#FFFFFF", textTransform: "none" }} variant="outlined" onClick={() => setFieldValue("environment", "dev")} disabled={devDisable}>dev</Button>
                                                        <Button sx={{ borderRadius: 5, borderColor: values.environment === "staging" ? "#000000" : "#666666", color: values.environment === "staging" ? "#000000" : "#666666", bgcolor: values.environment === "staging" ? "#E0E0E0" : "#FFFFFF", textTransform: "none" }} variant="outlined" onClick={() => setFieldValue("environment", "staging")} disabled={stageDisable}>staging</Button>
                                                        <Button sx={{ borderRadius: 5, borderColor: values.environment === "prod" ? "#000000" : "#666666", color: values.environment === "prod" ? "#000000" : "#666666", bgcolor: values.environment === "prod" ? "#E0E0E0" : "#FFFFFF", textTransform: "none" }} variant="outlined" onClick={() => setFieldValue("environment", "prod")} disabled={prodDisable}>prod</Button>
                                                    </Box>
                                                    <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="environment" component="div" /></div>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <CompulsoryFieldsLabel fieldName="Cluster Size" />
                                                    <Box display="flex" gap={2} mt="14px">
                                                        <Button sx={{ borderRadius: 5, borderColor: values.clusterSize === "small" ? "#000000" : "#666666", color: values.clusterSize === "small" ? "#000000" : "#666666", bgcolor: values.clusterSize === "small" ? "#E0E0E0" : "#FFFFFF", textTransform: "none" }} variant="outlined" onClick={() => setFieldValue("clusterSize", "small")}>small</Button>
                                                        <Button sx={{ borderRadius: 5, borderColor: values.clusterSize === "medium" ? "#000000" : "#666666", color: values.clusterSize === "medium" ? "#000000" : "#666666", bgcolor: values.clusterSize === "medium" ? "#E0E0E0" : "#FFFFFF", textTransform: "none" }} variant="outlined" onClick={() => setFieldValue("clusterSize", "medium")}>medium</Button>
                                                        <Button sx={{ borderRadius: 5, borderColor: values.clusterSize === "large" ? "#000000" : "#666666", color: values.clusterSize === "large" ? "#000000" : "#666666", bgcolor: values.clusterSize === "large" ? "#E0E0E0" : "#FFFFFF", textTransform: "none" }} variant="outlined" onClick={() => setFieldValue("clusterSize", "large")}>large</Button>
                                                    </Box>
                                                    <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="clusterSize" component="div" /></div>
                                                </Grid>
                                            </Grid>
                                        </div>

                                        <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
                                            <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}> Cluster Viewers and Application Owners </Typography>
                                            <Divider sx={{ marginBottom: "24px" }} />
                                            <Grid container spacing={4} style={{ marginBottom: "16px" }}>
                                                <Grid item xs={12} sm={6}>
                                                    <CompulsoryFieldsLabel fieldName="Cluster Viewers" />
                                                    <Autocomplete multiple options={users.map((e: any) => e.email)} filterSelectedOptions ChipProps={{ size: "small" }} freeSolo value={values.gcpClusterViewers} onChange={(e, newVal) => setFieldValue("gcpClusterViewers", newVal)} renderInput={(params) => (<TextField {...params} variant="filled" fullWidth placeholder="e.g. abc121@searce.com" size="small" hiddenLabel InputProps={{ ...params.InputProps, style: { borderRadius: "8px" }, disableUnderline: true }} />)} />
                                                    <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>{typeof getIn(errors, `gcpClusterViewers`) === "string" ? getIn(errors, `gcpClusterViewers`) : (getIn(errors, `gcpClusterViewers`) as string[])?.filter((e) => e != null)?.[0]}</div>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <CompulsoryFieldsLabel fieldName="Application Owners" />
                                                    <Autocomplete multiple options={users.map((e: any) => e.email)} filterSelectedOptions ChipProps={{ size: "small" }} freeSolo value={values.applicationRequesters} onChange={(e, newVal) => setFieldValue("applicationRequesters", newVal)} renderInput={(params) => (<TextField {...params} variant="filled" fullWidth placeholder="e.g. abc121@searce.com" size="small" hiddenLabel InputProps={{ ...params.InputProps, style: { borderRadius: "8px" }, disableUnderline: true }} />)} />
                                                    <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>{typeof getIn(errors, `applicationRequesters`) === "string" ? getIn(errors, `applicationRequesters`) : (getIn(errors, `applicationRequesters`) as string[])?.filter((e) => e != null)?.[0]}</div>
                                                </Grid>
                                            </Grid>
                                        </div>

                                        <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
                                            <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}> Cluster Tags / Labels </Typography>
                                            <Divider sx={{ marginBottom: "24px" }} />
                                            <Grid container spacing={4} style={{ marginBottom: "16px" }}>
                                                <Grid item xs={12} sm={6}><Typography gutterBottom fontFamily="Gilroy-Regular" fontSize="14px" ml="4px"> Key </Typography></Grid>
                                                <Grid item xs={12} sm={6}><Typography gutterBottom fontFamily="Gilroy-Regular" fontSize="14px" ml="4px"> Value </Typography></Grid>
                                            </Grid>
                                            <Grid container spacing={4}>
                                                <Grid item xs={12} sm={6}><TextField fullWidth placeholder="Enter key" size="medium" hiddenLabel disabled={true} value="cluster-owner" variant="filled" sx={{ marginBottom: "0px" }} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /></Grid>
                                                <Grid item xs={12} sm={6}><Box display="flex" alignItems="center" justifyContent="space-between"><TextField fullWidth placeholder="Enter value" size="medium" value={(isEdit && cluster.clusterOwner?.name ? (cluster.clusterOwner.name as string) : (user.name as string)).toLowerCase().replace(" ", "-")} hiddenLabel sx={{ marginBottom: "0px" }} variant="filled" disabled={true} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /></Box></Grid>
                                                <Grid item xs={12} sm={6}><TextField fullWidth placeholder="Enter key" size="medium" hiddenLabel disabled={true} value="department" variant="filled" sx={{ marginBottom: "0px" }} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /></Grid>
                                                <Grid item xs={12} sm={6}><Box display="flex" alignItems="center" justifyContent="space-between"><TextField fullWidth placeholder="Enter value" size="medium" value={values.departmentValue} hiddenLabel variant="filled" name="departmentValue" sx={{ "& .MuiFilledInput-root": { border: errors["departmentValue"] && touched["departmentValue"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } }, marginBottom: "0px" }} onChange={handleChange} onBlur={handleBlur} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="departmentValue" component="div" /></div></Box></Grid>
                                                <Grid item xs={12} sm={6}><TextField fullWidth placeholder="Enter key" size="medium" hiddenLabel disabled={true} value="cost-center" variant="filled" sx={{ marginBottom: "0px" }} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /></Grid>
                                                <Grid item xs={12} sm={6}><Box display="flex" alignItems="center" justifyContent="space-between"><TextField fullWidth placeholder="Enter value" size="medium" value={values.costCenterValue} hiddenLabel variant="filled" name="costCenterValue" sx={{ "& .MuiFilledInput-root": { border: errors["costCenterValue"] && touched["costCenterValue"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } }, marginBottom: "0px" }} onChange={handleChange} onBlur={handleBlur} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="costCenterValue" component="div" /></div></Box></Grid>
                                            </Grid>
                                            <Grid container spacing={4} mt="8px">
                                                {values.costLabelPairs.map((e: any, index: number) => (<React.Fragment key={e.id || index}>
                                                    <Grid item xs={12} sm={6}><TextField fullWidth placeholder="Enter key" size="medium" hiddenLabel value={e.cost} sx={{ "& .MuiFilledInput-root": { border: getIn(errors, `costLabelPairs[${index}].cost`) && getIn(touched, `costLabelPairs[${index}].cost`) ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } }, marginBottom: "0px" }} variant="filled" onChange={(event) => { const newCostLabelPairs = values.costLabelPairs.map((pair: any, pairIndex: number) => { if (index === pairIndex) { return { ...pair, cost: event.target.value || "" }; } return pair; }); setFieldValue("costLabelPairs", newCostLabelPairs); }} onBlur={handleBlur} name={`costLabelPairs[${index}].cost`} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name={`costLabelPairs[${index}].cost`} /></div></Grid>
                                                    <Grid item xs={12} sm={6}><Box display="flex" alignItems="center" justifyContent="space-between"><TextField fullWidth placeholder="Enter value" size="medium" sx={{ "& .MuiFilledInput-root": { border: getIn(errors, `costLabelPairs[${index}].label`) && getIn(touched, `costLabelPairs[${index}].label`) ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } }, marginBottom: "0px" }} value={e.label} hiddenLabel variant="filled" onChange={(event) => { const newCostLabelPairs = values.costLabelPairs.map((pair: any, pairIndex: number) => { if (index === pairIndex) { return { ...pair, label: event.target.value || "" }; } return pair; }); setFieldValue("costLabelPairs", newCostLabelPairs); }} onBlur={handleBlur} name={`costLabelPairs[${index}].label`} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /><Button sx={{ marginLeft: "8px" }} onClick={() => { let clpArr = values.costLabelPairs.filter((pair: any, pairIndex: number) => { return index !== pairIndex; }); setFieldValue("costLabelPairs", clpArr); }}><img alt="close-icon" src="/assets/close-icon.svg" /></Button></Box><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name={`costLabelPairs[${index}].label`} /></div></Grid>
                                                </React.Fragment>))}
                                            </Grid>
                                            <Button variant="text" sx={{ color: "#000000", textTransform: "none", mt: "16px" }} onClick={() => setFieldValue("costLabelPairs", [...values.costLabelPairs, { cost: "", label: "", id: crypto.randomUUID() }])}>+ Add Key Value Pair</Button>
                                        </div>

                                        <Box display="flex" justifyContent="flex-end" gap={2}>
                                            <Button sx={{ color: "black", borderColor: "black", fontWeight: "bold", textTransform: "none", px: 4 }} variant="outlined" size="large" onClick={() => navigate("/clusters")}>Cancel</Button>
                                            <Button sx={{ backgroundColor: "black", color: "white", fontWeight: "bold", "&:hover": { backgroundColor: "white", color: "black", borderColor: "black" }, textTransform: "none", px: 4 }} variant="contained" size="large" type="submit" disabled={!isValid || !dirty || isViewer}>
                                                {isEdit ? "Save Changes" : "Add New Cluster"}
                                            </Button>
                                        </Box>
                                    </Box>
                                </Form>
                            )}
                        </Formik>
                    )}
                </Grid>
                <Grid item xs={1}></Grid>
            </Grid>
        </Container>
    );
};

const ClusterForm: React.FC<ClusterFormProps> = ({ isEdit }) => {
    const theme = useTheme();
    const getInitialDrawerState = () => {
        if (typeof window !== 'undefined') {
            const storedState = localStorage.getItem('isFaradayDrawerOpen');
            return storedState !== null ? JSON.parse(storedState) : true;
        }
        return true;
    };
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);
    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => { setIsDrawerActuallyOpen(isOpenFromDrawer); };
    const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;
    const copyIsEdit = isEdit;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer index={"clusters"} onStateChange={handleDrawerStateChange} />
            <Box component="main" sx={{ flexGrow: 1, p: 0, transition: theme.transitions.create('margin', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen, }), marginLeft: `${currentMarginLeft}vh` }}>
                <ClusterFormContent isEdit={copyIsEdit} />
            </Box>
        </Box>
    );
};

export default ClusterForm;

// import React, { useState } from "react";
// import {
//     Box,
//     Button,
//     Container,
//     Grid,
//     TextField,
//     Typography,
//     MenuItem,
//     IconButton,
//     Breadcrumbs,
//     Link,
//     Divider,
//     Autocomplete,
//     useTheme,
//     ToggleButton,
//     ToggleButtonGroup,
// } from "@mui/material";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import { useNavigate, useParams } from "react-router-dom";
// import { Formik, Form, ErrorMessage, getIn } from "formik";
// import * as Yup from "yup";
// import CompulsoryFieldsLabel from "../../helpers/CompulsoryFields";
// import queryGraphql from "../../helpers/ApiInterface";
// import { useAuth } from "../../helpers/AuthProvider";
// import MiniBot from "../MiniBot";
// import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../MainDrawer";

// // --- Cloud Provider Configuration ---

// const gcpclusterTypeList = ["Autopilot", "Standard"];
// const awsclusterTypeList = ["Auto Mode", "Managed Node", "Fargate"];
// const azureClusterTypes = ["Standard AKS", "AKS with Virtual Nodes"];
// const azurePricingTiers = ["Free", "Standard"];

// // Data for the new provider toggle buttons
// const providerDisplayData = [
//     { value: 'gcp', label: 'Google Cloud Platform', icon: '/assets/gcp_mini_icon.svg' },
//     { value: 'aws', label: 'Amazon Web Services', icon: '/assets/aws_mini_icon.svg' },
//     { value: 'azure', label: 'Azure', icon: '/assets/azure_mini_icon.svg' }
// ];

// // --- Region Lists ---

// const gcpregionList = [ "africa-south1", "asia-east1", "asia-east2", "asia-northeast1", "asia-northeast2", "asia-northeast3", "asia-south1", "asia-south2", "asia-southeast1", "asia-southeast2", "australia-southeast1", "australia-southeast2", "europe-central2", "europe-north1", "europe-southwest1", "europe-west1", "europe-west10", "europe-west12", "europe-west2", "europe-west3", "europe-west4", "europe-west6", "europe-west8", "europe-west9", "me-central1", "me-central2", "me-west1", "northamerica-northeast1", "northamerica-northeast2", "southamerica-east1", "southamerica-west1", "us-central1", "us-east1", "us-east4", "us-east5", "us-south1", "us-west1", "us-west2", "us-west3", "us-west4"];
// const awsregionList = [ "us-east-2", "us-east-1", "us-west-1", "us-west-2", "af-south-1", "ap-east-1", "ap-south-2", "ap-southeast-3", "ap-southeast-5", "ap-southeast-4", "ap-south-1", "ap-northeast-3", "ap-northeast-2", "ap-southeast-1", "ap-southeast-2", "ap-southeast-7", "ap-northeast-1", "ca-central-1", "ca-west-1", "eu-central-1", "eu-west-1", "eu-west-2", "eu-south-1", "eu-west-3", "eu-south-2", "eu-north-1", "eu-central-2", "il-central-1", "mx-central-1", "me-south-1", "me-central-1", "sa-east-1", "us-gov-east-1", "us-gov-west-1"];
// const azureRegionList = ["eastus", "eastus2", "westus", "westus2", "centralus", "northcentralus", "southcentralus", "westcentralus", "canadacentral", "canadaeast", "brazilsouth", "northeurope", "westeurope", "uksouth", "ukwest", "francecentral", "germanywestcentral", "switzerlandnorth", "uaenorth", "japaneast", "japanwest", "koreacentral", "koreasouth", "southeastasia", "australiaeast"];


// // --- Validation Schema ---

// const validationSchema = Yup.object().shape({
//     cloudProvider: Yup.string().required("This field is required").oneOf(["gcp", "aws", "azure"], "Invalid cloud provider"),
//     clusterName: Yup.string().required("This field is required").strict().lowercase("This field must be in lowercase").matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric").max(20, "This field must not exceed 20 characters"),
//     gcpProjectId: Yup.string().required("This field is required").strict().lowercase("This field must be in lowercase").matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric").max(30, "This field must not exceed 30 characters"),
//     region: Yup.string().required("This field is required").matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric").strict(),
//     gcpClusterViewers: Yup.array().of(Yup.string().required("This field is required").matches(/^[a-zA-Z0-9._%+-]+@searce\.com$/, "Invalid domain").strict()).required("This field is required").min(1, "This field is required").strict(),
//     applicationRequesters: Yup.array().of(Yup.string().required("This field is required").matches(/^[a-zA-Z0-9._%+-]+@searce\.com$/, "Invalid domain").strict()).required("This field is required").min(1, "This field is required").strict(),
//     costLabelPairs: Yup.array().of(Yup.object({
//         cost: Yup.string().required("This field is required").strict().lowercase("This field must be in lowercase").matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric").max(20, "This field must not exceed 20 characters"),
//         label: Yup.string().required("This field is required").strict().lowercase("This field must be in lowercase").matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric").max(20, "This field must not exceed 20 characters"),
//     })).required("This field is required"),
//     environment: Yup.string().required("This field is required").oneOf(["dev", "staging", "prod"]),
//     clusterSize: Yup.string().required("This field is required").oneOf(["small", "medium", "large"]),
//     clusterType: Yup.string().required("This field is required"),
//     departmentValue: Yup.string().required("This field is required").strict().lowercase("This field must be in lowercase").matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric").max(30, "This field must not exceed 30 characters"),
//     costCenterValue: Yup.string().required("This field is required").strict().lowercase("This field must be in lowercase").matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric").max(20, "This field must not exceed 20 characters"),
//     // Azure specific validation
//     resourceGroup: Yup.string().when("cloudProvider", {
//         is: 'azure',
//         then: () => Yup.string().required("Resource Group is required for Azure"),
//     }),
//     aksPricingTier: Yup.string().when("cloudProvider", {
//         is: 'azure',
//         then: () => Yup.string().required("Pricing Tier is required for Azure"),
//     }),
// });

// interface ClusterFormProps {
//     isEdit: boolean;
// }
// interface ClusterFormContentProps {
//     isEdit: boolean;
// }
// interface oneCluster {
//     id: string;
//     clusterName: string;
//     environment: string;
//     status: string;
// }
// const ClusterFormContent: React.FC<ClusterFormContentProps> = ({ isEdit }) => {
//     const navigate = useNavigate();
//     const [users, setUsers] = useState([]);
//     const { user } = useAuth();
//     const isViewer = user.roles?.some((role: string) => role === "viewer") || false;
//     const [cluster, setCluster] = useState<any>({});
//     const [clusters, setClusters] = useState<any>({});
//     const { id } = useParams();
//     const [devDisable, setDevDisable] = useState<boolean>(false);
//     const [stageDisable, setStageDisable] = useState<boolean>(false);
//     const [prodDisable, setProdDisable] = useState<boolean>(false);
//     const [ClusterName, setClusterName] = useState<string>();
//     const [dummy, setDummy] = useState<any>();

//     React.useEffect(() => {
//         const fetchData = async () => {
//             try {
//                 const data = await queryGraphql(
//                     `
//                             query($clusterId: String!){
//                             cluster(id:$clusterId) {
//                             id,
//                             clusterName,
//                             cloudProvider, 
//                             clusterOwner{
//                             id,
//                             name,
//                             googleProfilePicture
//                             },
//                             gcpProjectId,
//                             environment,
//                             region,
//                             clusterSize,
//                             clusterType,
//                             gcpClusterViewers,
//                             applicationRequesters,
//                             costLabelPairs{
//                             cost,
//                             label
//                             },
//                             status,
//                             createdAt,
//                             resourceGroup,
//                             aksPricingTier
//                             }
//                             }
// `,
//                     { clusterId: id }
//                 );
//                 if (data.cluster) {
//                     setCluster(data.cluster);
//                 }
//             } catch (error) {
//                 console.log(error);
//             }
//         };

//         if (isEdit && id) {
//             fetchData();
//         }
//     }, [id, isEdit]);

//     React.useEffect(() => {
//         const fetchData = async () => {
//             try {
//                 const data = await queryGraphql(`
//                             query Clusters {
//                                 clusters {
//                                     id
//                                     clusterName
//                                     environment
//                                     status
//                                 }
//                                 }
//                                 `);
//                 if (data.clusters) {
//                     setClusters(data.clusters.filter((e: any) => e.status !== "rejected"));
//                 }
//                 setDevDisable(false);
//                 setStageDisable(true);
//                 setProdDisable(true);
//                 clusters.forEach((item: oneCluster) => {

//                     (ClusterName === item.clusterName && "dev" === item.environment) ? setDevDisable(true) : (ClusterName === item.clusterName && "staging" === item.environment) ? setStageDisable(true) : (ClusterName === item.clusterName && "prod" === item.environment ? setProdDisable(true) : setDummy(true));

//                     if (ClusterName === item.clusterName && "dev" === item.environment) {
//                         setDevDisable(true);
//                         setStageDisable(false);
//                         setProdDisable(false);
//                     }
//                     if (ClusterName === item.clusterName && "staging" === item.environment) {
//                         setDevDisable(true);
//                         setStageDisable(true);
//                         setProdDisable(false);
//                     }
//                     if (ClusterName === item.clusterName && "prod" === item.environment) {
//                         setDevDisable(true);
//                         setStageDisable(true);
//                         setProdDisable(true);
//                     }
//                 });
//             } catch (error) {
//                 console.log(error);
//             }
//         };
//         fetchData();
//     }, [ClusterName, clusters]);

//     React.useEffect(() => {
//         const fetchData = async () => {
//             try {
//                 const data = await queryGraphql(`
//                             query{
//                             users {
//                             id,
//                             email
//                             }
//                             }
// `);
//                 if (data.users) {
//                     setUsers(data.users);
//                 }
//             } catch (error) {
//                 console.log(error);
//             }
//         };

//         fetchData();
//     }, []);

//     return (
//         <Container
//             disableGutters
//             maxWidth={false}
//             sx={{ padding: "24px", bgcolor: "#fafafa", minHeight: "100vh" }}
//         >
//             <MiniBot />
//             <Box sx={{ mb: 4 }}>
//                 <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
//                     <Link underline="hover" color="inherit" href="/clusters">
//                         Clusters
//                     </Link>
//                     <Typography color="text.primary">
//                         {isEdit ? "Edit Cluster Details" : "Add New Cluster"}
//                     </Typography>
//                 </Breadcrumbs>
//                 <Box display="flex" alignItems="center" mt={2}>
//                     <IconButton
//                         edge="start"
//                         aria-label="back"
//                         color="inherit"
//                         onClick={() => navigate("/clusters")}
//                     >
//                         <ArrowBackIosNewIcon fontSize="small" />
//                     </IconButton>
//                     <Typography fontFamily="Gilroy-Bold" fontSize="18px">
//                         {isEdit ? "Edit Cluster Details" : "Add New Cluster"}
//                     </Typography>
//                 </Box>
//             </Box>
//             <Grid container>
//                 <Grid item xs={1}></Grid>
//                 <Grid item xs={10}>
//                     {((isEdit && cluster.id) || !isEdit) && (
//                         <Formik
//                             initialValues={{
//                                 cloudProvider: isEdit ? (cluster.cloudProvider || "gcp") : "gcp",
//                                 clusterName: isEdit ? cluster.clusterName : "",
//                                 gcpProjectId: isEdit ? cluster.gcpProjectId : "",
//                                 region: isEdit ? cluster.region : "",
//                                 gcpClusterViewers: isEdit ? cluster.gcpClusterViewers : [],
//                                 applicationRequesters: isEdit ? cluster.applicationRequesters : [],
//                                 costLabelPairs: isEdit ? cluster.costLabelPairs?.filter((e: any) => !["department", "cost-center", "cluster-owner"].includes(e.cost)) || [] : [],
//                                 environment: isEdit ? cluster.environment : "",
//                                 clusterSize: isEdit ? cluster.clusterSize : "",
//                                 clusterType: isEdit ? cluster.clusterType : "",
//                                 departmentValue: isEdit ? cluster.costLabelPairs?.find((elem: any) => elem.cost === "department")?.label || "" : "",
//                                 costCenterValue: isEdit ? cluster.costLabelPairs?.find((elem: any) => elem.cost === "cost-center")?.label || "" : "",
//                                 // Azure fields
//                                 resourceGroup: isEdit ? cluster.resourceGroup : "",
//                                 aksPricingTier: isEdit ? cluster.aksPricingTier : "",
//                             }}
//                             validationSchema={validationSchema}
//                             onSubmit={async (values) => {
//                                 let keyValuePairs = [];
//                                 keyValuePairs.push(
//                                     { cost: "cluster-owner", label: (user.name as string).toLowerCase().replace(" ", "-") },
//                                     { cost: "department", label: values.departmentValue },
//                                     { cost: "cost-center", label: values.costCenterValue },
//                                     ...values.costLabelPairs.map((e: any) => ({ cost: e.cost, label: e.label }))
//                                 );

//                                 const inputPayload: any = {
//                                     cloudProvider: values.cloudProvider,
//                                     clusterName: values.clusterName,
//                                     gcpProjectId: values.gcpProjectId,
//                                     region: values.region,
//                                     clusterType: values.clusterType,
//                                     environment: values.environment,
//                                     clusterSize: values.clusterSize, // Now included for all
//                                     gcpClusterViewers: values.gcpClusterViewers,
//                                     applicationRequesters: values.applicationRequesters,
//                                     costLabelPairs: keyValuePairs,
//                                 };

//                                 if (values.cloudProvider === 'azure') {
//                                     inputPayload.resourceGroup = values.resourceGroup;
//                                     inputPayload.aksPricingTier = values.aksPricingTier;
//                                 }

//                                 if (!isEdit) {
//                                     const data = await queryGraphql(
//                                         `mutation CreateCluster($input: ClusterCreateInput) { createCluster(input: $input) { id } }`,
//                                         { input: inputPayload }
//                                     );
//                                     if (data.createCluster.id) {
//                                         await queryGraphql(`mutation Mutation($input: ResourceStatusCreateInput) { createResourceStatus(input: $input) { id } }`, {
//                                             input: {
//                                                 currentStatus: "pending",
//                                                 resourceEnvironment: values.environment,
//                                                 resourceId: data.createCluster.id,
//                                                 resourceName: values.clusterName,
//                                                 resourceType: "cluster"
//                                             }
//                                         });
//                                         navigate("/clusters");
//                                     }
//                                 } else {
//                                     const data = await queryGraphql(
//                                         `mutation UpdateCluster($input: ClusterUpdateInput) { updateCluster(input: $input) { id } }`,
//                                         { input: { id: id, ...inputPayload } }
//                                     );
//                                     if (data.updateCluster.id) {
//                                         await queryGraphql(`mutation Mutation($input: ResourceStatusUpdateInput) { updateResourceStatus(input: $input) { id } }`, {
//                                             input: {
//                                                 currentStatus: "pending",
//                                                 resourceId: id,
//                                                 resourceName: values.clusterName
//                                             }
//                                         });
//                                         navigate("/clusters");
//                                     }
//                                 }
//                             }}
//                         >
//                             {({
//                                 handleChange,
//                                 handleBlur,
//                                 values,
//                                 setFieldValue,
//                                 errors,
//                                 touched,
//                                 isValid,
//                                 dirty,
//                             }) => {
//                                 return (
//                                     <Form>
//                                         <Box>
//                                             <Typography fontFamily="Gilroy-Medium" fontSize="16px" mb={4}>
//                                                 {isEdit ? "Make changes to below details and save" : "Fill the below details to add cluster"}
//                                             </Typography>
                                            
//                                             {/* --- Cloud Provider Section --- */}
//                                             <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
//                                                 <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}> Cloud Provider Details </Typography>
//                                                 <Divider sx={{ marginBottom: "24px" }} />
//                                                 <Grid container spacing={4} style={{ marginBottom: "16px" }}>
//                                                     <Grid item xs={12} sm={12}>
//                                                         <CompulsoryFieldsLabel fieldName="Cloud Provider" />
//                                                         <ToggleButtonGroup
//                                                             value={values.cloudProvider}
//                                                             exclusive
//                                                             onChange={(event, newProvider) => {
//                                                                 if (newProvider !== null) {
//                                                                     setFieldValue('cloudProvider', newProvider);
//                                                                     setFieldValue("region", "");
//                                                                     setFieldValue("clusterType", "");
//                                                                     setFieldValue("clusterSize", "");
//                                                                     setFieldValue("environment", "");
//                                                                 }
//                                                             }}
//                                                             aria-label="cloud provider"
//                                                             sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}
//                                                         >
//                                                             {providerDisplayData.map((p) => (
//                                                                 <ToggleButton
//                                                                     key={p.value}
//                                                                     value={p.value}
//                                                                     aria-label={p.label}
//                                                                     sx={{
//                                                                         display: 'flex', gap: 1, alignItems: 'center',
//                                                                         textTransform: 'none', fontFamily: 'Gilroy-Medium',
//                                                                         borderRadius: '24px !important',
//                                                                         border: '1px solid #ccc !important', px: 2.5, py: 1,
//                                                                         '&.Mui-selected': { borderColor: '#000 !important', backgroundColor: 'rgba(0, 0, 0, 0.08)' },
//                                                                         '&.Mui-selected:hover': { backgroundColor: 'rgba(0, 0, 0, 0.12)' }
//                                                                     }}
//                                                                 >
//                                                                     <img src={p.icon} alt={`${p.label} icon`} style={{ width: '20px', height: '20px' }} />
//                                                                     {p.label}
//                                                                 </ToggleButton>
//                                                             ))}
//                                                         </ToggleButtonGroup>
//                                                         <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                             <ErrorMessage name="cloudProvider" component="div" />
//                                                         </div>
//                                                     </Grid>
//                                                 </Grid>
//                                             </div>

//                                             {/* --- Dynamic Form Sections --- */}
//                                             <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
//                                                 <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}> Basic Information </Typography>
//                                                 <Divider sx={{ marginBottom: "24px" }} />
//                                                 <Grid container spacing={4} style={{ marginBottom: "16px" }}>
//                                                     <Grid item xs={12} sm={6}>
//                                                         <CompulsoryFieldsLabel fieldName="Cluster Name" />
//                                                         <TextField sx={{ "& .MuiFilledInput-root": { border: errors["clusterName"] && touched["clusterName"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }} variant="filled" fullWidth name="clusterName" value={values.clusterName} onChange={(e) => { handleChange(e); setClusterName(e.target.value); }} onBlur={handleBlur} placeholder="Enter name here" size="medium" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
//                                                         <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="clusterName" component="div" /></div>
//                                                     </Grid>

//                                                     <Grid item xs={12} sm={6}>
//                                                         <CompulsoryFieldsLabel fieldName={values.cloudProvider === 'aws' ? "AWS Account ID" : values.cloudProvider === 'azure' ? "Subscription ID" : "GCP Project ID"} />
//                                                         <TextField variant="filled" fullWidth name="gcpProjectId" value={values.gcpProjectId} onChange={handleChange} onBlur={handleBlur} placeholder={values.cloudProvider === 'aws' ? "Enter AWS Account ID" : values.cloudProvider === 'azure' ? "Enter Subscription ID" : "Enter GCP Project ID"} size="medium" sx={{ "& .MuiFilledInput-root": { border: errors["gcpProjectId"] && touched["gcpProjectId"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }} hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
//                                                         <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="gcpProjectId" component="div" /></div>
//                                                     </Grid>

//                                                     {values.cloudProvider === 'azure' && (
//                                                         <Grid item xs={12} sm={6}>
//                                                             <CompulsoryFieldsLabel fieldName="Resource Group" />
//                                                             <TextField name="resourceGroup" value={values.resourceGroup} onChange={handleChange} onBlur={handleBlur} placeholder="Enter Resource Group" variant="filled" fullWidth hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
//                                                             <div style={{ color: "red", marginTop: "4px" }}><ErrorMessage name="resourceGroup" /></div>
//                                                         </Grid>
//                                                     )}

//                                                     <Grid item xs={12} sm={6}>
//                                                         <CompulsoryFieldsLabel fieldName="Cluster Type" />
//                                                         <TextField select placeholder="Select" name="clusterType" value={values.clusterType} onChange={handleChange} onBlur={handleBlur} variant="filled" fullWidth hiddenLabel size="medium" sx={{ "& .MuiFilledInput-root": { border: errors["clusterType"] && touched["clusterType"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
//                                                             {(values.cloudProvider === 'gcp' ? gcpclusterTypeList : values.cloudProvider === 'aws' ? awsclusterTypeList : azureClusterTypes).map((e) => (
//                                                                 <MenuItem key={e} value={e}>{e}</MenuItem>
//                                                             ))}
//                                                         </TextField>
//                                                         <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="clusterType" component="div" /></div>
//                                                     </Grid>
                                                    
//                                                      <Grid item xs={12} sm={6}>
//                                                         <CompulsoryFieldsLabel fieldName="Region" />
//                                                         <Autocomplete options={values.cloudProvider === 'gcp' ? gcpregionList : values.cloudProvider === 'aws' ? awsregionList : azureRegionList} filterSelectedOptions value={values.region || null} onChange={(e, newVal) => setFieldValue("region", newVal || "")} renderInput={(params) => (<TextField {...params} variant="filled" fullWidth placeholder="Select" size="medium" hiddenLabel sx={{ "& .MuiFilledInput-root": { border: errors["region"] && touched["region"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } } }} InputProps={{ ...params.InputProps, style: { borderRadius: "8px" }, disableUnderline: true }} />)} />
//                                                         <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>{getIn(errors, `region`)}</div>
//                                                     </Grid>

//                                                     {values.cloudProvider === 'azure' && (
//                                                         <Grid item xs={12} sm={6}>
//                                                             <CompulsoryFieldsLabel fieldName="AKS pricing tier" />
//                                                             <TextField select fullWidth variant="filled" hiddenLabel name="aksPricingTier" value={values.aksPricingTier} onChange={handleChange} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
//                                                                 {azurePricingTiers.map(option => <MenuItem key={option} value={option}>{option}</MenuItem>)}
//                                                             </TextField>
//                                                              <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="aksPricingTier" /></div>
//                                                         </Grid>
//                                                     )}
                                                    
//                                                     <Grid item xs={12} sm={6}>
//                                                         <CompulsoryFieldsLabel fieldName="Environment" />
//                                                         <Box display="flex" gap={2} mt="14px">
//                                                             <Button sx={{ borderRadius: 5, borderColor: values.environment === "dev" ? "#000000" : "#666666", color: values.environment === "dev" ? "#000000" : "#666666", bgcolor: values.environment === "dev" ? "#E0E0E0" : "#FFFFFF", textTransform: "none" }} variant="outlined" onClick={() => setFieldValue("environment", "dev")} disabled={devDisable}>dev</Button>
//                                                             <Button sx={{ borderRadius: 5, borderColor: values.environment === "staging" ? "#000000" : "#666666", color: values.environment === "staging" ? "#000000" : "#666666", bgcolor: values.environment === "staging" ? "#E0E0E0" : "#FFFFFF", textTransform: "none" }} variant="outlined" onClick={() => setFieldValue("environment", "staging")} disabled={stageDisable}>staging</Button>
//                                                             <Button sx={{ borderRadius: 5, borderColor: values.environment === "prod" ? "#000000" : "#666666", color: values.environment === "prod" ? "#000000" : "#666666", bgcolor: values.environment === "prod" ? "#E0E0E0" : "#FFFFFF", textTransform: "none" }} variant="outlined" onClick={() => setFieldValue("environment", "prod")} disabled={prodDisable}>prod</Button>
//                                                         </Box>
//                                                         <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="environment" component="div" /></div>
//                                                     </Grid>
                                                    
//                                                     <Grid item xs={12} sm={6}>
//                                                         <CompulsoryFieldsLabel fieldName="Cluster Size" />
//                                                         <Box display="flex" gap={2} mt="14px">
//                                                             <Button sx={{ borderRadius: 5, borderColor: values.clusterSize === "small" ? "#000000" : "#666666", color: values.clusterSize === "small" ? "#000000" : "#666666", bgcolor: values.clusterSize === "small" ? "#E0E0E0" : "#FFFFFF", textTransform: "none" }} variant="outlined" onClick={() => setFieldValue("clusterSize", "small")}>small</Button>
//                                                             <Button sx={{ borderRadius: 5, borderColor: values.clusterSize === "medium" ? "#000000" : "#666666", color: values.clusterSize === "medium" ? "#000000" : "#666666", bgcolor: values.clusterSize === "medium" ? "#E0E0E0" : "#FFFFFF", textTransform: "none" }} variant="outlined" onClick={() => setFieldValue("clusterSize", "medium")}>medium</Button>
//                                                             <Button sx={{ borderRadius: 5, borderColor: values.clusterSize === "large" ? "#000000" : "#666666", color: values.clusterSize === "large" ? "#000000" : "#666666", bgcolor: values.clusterSize === "large" ? "#E0E0E0" : "#FFFFFF", textTransform: "none" }} variant="outlined" onClick={() => setFieldValue("clusterSize", "large")}>large</Button>
//                                                         </Box>
//                                                         <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="clusterSize" component="div" /></div>
//                                                     </Grid>
//                                                 </Grid>
//                                             </div>

//                                             {/* Other sections remain unchanged */}

//                                             <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
//                                                 <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}> Cluster Viewers and Application Owners </Typography>
//                                                 <Divider sx={{ marginBottom: "24px" }} />
//                                                 <Grid container spacing={4} style={{ marginBottom: "16px" }}>
//                                                     <Grid item xs={12} sm={6}>
//                                                         <CompulsoryFieldsLabel fieldName="Cluster Viewers" />
//                                                         <Autocomplete multiple options={users.map((e: any) => e.email)} filterSelectedOptions ChipProps={{ size: "small" }} freeSolo value={values.gcpClusterViewers} onChange={(e, newVal) => setFieldValue("gcpClusterViewers", newVal)} renderInput={(params) => (<TextField {...params} variant="filled" fullWidth placeholder="e.g. abc121@searce.com" size="small" hiddenLabel InputProps={{ ...params.InputProps, style: { borderRadius: "8px" }, disableUnderline: true }} />)} />
//                                                         <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>{typeof getIn(errors, `gcpClusterViewers`) === "string" ? getIn(errors, `gcpClusterViewers`) : (getIn(errors, `gcpClusterViewers`) as string[])?.filter((e) => e != null)?.[0]}</div>
//                                                     </Grid>
//                                                     <Grid item xs={12} sm={6}>
//                                                         <CompulsoryFieldsLabel fieldName="Application Owners" />
//                                                         <Autocomplete multiple options={users.map((e: any) => e.email)} filterSelectedOptions ChipProps={{ size: "small" }} freeSolo value={values.applicationRequesters} onChange={(e, newVal) => setFieldValue("applicationRequesters", newVal)} renderInput={(params) => (<TextField {...params} variant="filled" fullWidth placeholder="e.g. abc121@searce.com" size="small" hiddenLabel InputProps={{ ...params.InputProps, style: { borderRadius: "8px" }, disableUnderline: true }} />)} />
//                                                         <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>{typeof getIn(errors, `applicationRequesters`) === "string" ? getIn(errors, `applicationRequesters`) : (getIn(errors, `applicationRequesters`) as string[])?.filter((e) => e != null)?.[0]}</div>
//                                                     </Grid>
//                                                 </Grid>
//                                             </div>

//                                             <div style={{ backgroundColor: "white", padding: "24px", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", marginBottom: "24px" }}>
//                                                 <Typography fontFamily="Gilroy-Medium" fontSize="14px" style={{ marginBottom: "16px" }}> Cluster Tags / Labels </Typography>
//                                                 <Divider sx={{ marginBottom: "24px" }} />
//                                                 <Grid container spacing={4} style={{ marginBottom: "16px" }}>
//                                                     <Grid item xs={12} sm={6}><Typography gutterBottom fontFamily="Gilroy-Regular" fontSize="14px" ml="4px"> Key </Typography></Grid>
//                                                     <Grid item xs={12} sm={6}><Typography gutterBottom fontFamily="Gilroy-Regular" fontSize="14px" ml="4px"> Value </Typography></Grid>
//                                                 </Grid>
//                                                 <Grid container spacing={4}>
//                                                     <Grid item xs={12} sm={6}><TextField fullWidth placeholder="Enter key" size="medium" hiddenLabel disabled={true} value="cluster-owner" variant="filled" sx={{ marginBottom: "0px" }} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /></Grid>
//                                                     <Grid item xs={12} sm={6}><Box display="flex" alignItems="center" justifyContent="space-between"><TextField fullWidth placeholder="Enter value" size="medium" value={(isEdit && cluster.clusterOwner?.name ? (cluster.clusterOwner.name as string) : (user.name as string)).toLowerCase().replace(" ", "-")} hiddenLabel sx={{ marginBottom: "0px" }} variant="filled" disabled={true} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /></Box></Grid>
//                                                     <Grid item xs={12} sm={6}><TextField fullWidth placeholder="Enter key" size="medium" hiddenLabel disabled={true} value="department" variant="filled" sx={{ marginBottom: "0px" }} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /></Grid>
//                                                     <Grid item xs={12} sm={6}><Box display="flex" alignItems="center" justifyContent="space-between"><TextField fullWidth placeholder="Enter value" size="medium" value={values.departmentValue} hiddenLabel variant="filled" name="departmentValue" sx={{ "& .MuiFilledInput-root": { border: errors["departmentValue"] && touched["departmentValue"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } }, marginBottom: "0px" }} onChange={handleChange} onBlur={handleBlur} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="departmentValue" component="div" /></div></Box></Grid>
//                                                     <Grid item xs={12} sm={6}><TextField fullWidth placeholder="Enter key" size="medium" hiddenLabel disabled={true} value="cost-center" variant="filled" sx={{ marginBottom: "0px" }} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /></Grid>
//                                                     <Grid item xs={12} sm={6}><Box display="flex" alignItems="center" justifyContent="space-between"><TextField fullWidth placeholder="Enter value" size="medium" value={values.costCenterValue} hiddenLabel variant="filled" name="costCenterValue" sx={{ "& .MuiFilledInput-root": { border: errors["costCenterValue"] && touched["costCenterValue"] ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } }, marginBottom: "0px" }} onChange={handleChange} onBlur={handleBlur} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="costCenterValue" component="div" /></div></Box></Grid>
//                                                 </Grid>
//                                                 <Grid container spacing={4} mt="8px">
//                                                     {values.costLabelPairs.map((e: any, index: number) => (<React.Fragment key={e.id || index}>
//                                                         <Grid item xs={12} sm={6}><TextField fullWidth placeholder="Enter key" size="medium" hiddenLabel value={e.cost} sx={{ "& .MuiFilledInput-root": { border: getIn(errors, `costLabelPairs[${index}].cost`) && getIn(touched, `costLabelPairs[${index}].cost`) ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } }, marginBottom: "0px" }} variant="filled" onChange={(event) => { const newCostLabelPairs = values.costLabelPairs.map((pair: any, pairIndex: number) => { if (index === pairIndex) { return { ...pair, cost: event.target.value || "" }; } return pair; }); setFieldValue("costLabelPairs", newCostLabelPairs); }} onBlur={handleBlur} name={`costLabelPairs[${index}].cost`} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name={`costLabelPairs[${index}].cost`} /></div></Grid>
//                                                         <Grid item xs={12} sm={6}><Box display="flex" alignItems="center" justifyContent="space-between"><TextField fullWidth placeholder="Enter value" size="medium" sx={{ "& .MuiFilledInput-root": { border: getIn(errors, `costLabelPairs[${index}].label`) && getIn(touched, `costLabelPairs[${index}].label`) ? "1px solid red" : "1px solid #00000000", "&.Mui-focused": { backgroundColor: "white", border: "1px solid black" } }, marginBottom: "0px" }} value={e.label} hiddenLabel variant="filled" onChange={(event) => { const newCostLabelPairs = values.costLabelPairs.map((pair: any, pairIndex: number) => { if (index === pairIndex) { return { ...pair, label: event.target.value || "" }; } return pair; }); setFieldValue("costLabelPairs", newCostLabelPairs); }} onBlur={handleBlur} name={`costLabelPairs[${index}].label`} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /><Button sx={{ marginLeft: "8px" }} onClick={() => { let clpArr = values.costLabelPairs.filter((pair: any, pairIndex: number) => { return index !== pairIndex; }); setFieldValue("costLabelPairs", clpArr); }}><img alt="close-icon" src="/assets/close-icon.svg" /></Button></Box><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name={`costLabelPairs[${index}].label`} /></div></Grid>
//                                                     </React.Fragment>))}
//                                                 </Grid>
//                                                 <Button variant="text" sx={{ color: "#000000", textTransform: "none", mt: "16px" }} onClick={() => setFieldValue("costLabelPairs", [...values.costLabelPairs, { cost: "", label: "", id: crypto.randomUUID() }])}>+ Add Key Value Pair</Button>
//                                             </div>

//                                             <Box display="flex" justifyContent="flex-end" gap={2}>
//                                                 <Button sx={{ color: "black", borderColor: "black", fontWeight: "bold", textTransform: "none", px: 4 }} variant="outlined" size="large" onClick={() => navigate("/clusters")}>Cancel</Button>
//                                                 <Button sx={{ backgroundColor: "black", color: "white", fontWeight: "bold", "&:hover": { backgroundColor: "white", color: "black", borderColor: "black" }, textTransform: "none", px: 4 }} variant="contained" size="large" type="submit" disabled={!isValid || !dirty || isViewer}>
//                                                     {isEdit ? "Save Changes" : "Add New Cluster"}
//                                                 </Button>
//                                             </Box>
//                                         </Box>
//                                     </Form>
//                                 );
//                             }}
//                         </Formik>
//                     )}
//                 </Grid>
//                 <Grid item xs={1}></Grid>
//             </Grid>
//         </Container>
//     );
// };

// const ClusterForm: React.FC<ClusterFormProps> = ({ isEdit }) => {
//     const theme = useTheme();

//     const getInitialDrawerState = () => {
//         if (typeof window !== 'undefined') {
//             const storedState = localStorage.getItem('isFaradayDrawerOpen');
//             return storedState !== null ? JSON.parse(storedState) : true;
//         }
//         return true;
//     };
//     const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);

//     const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
//         setIsDrawerActuallyOpen(isOpenFromDrawer);
//     };

//     const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;
//     const copyIsEdit = isEdit;

//     return (
//         <Box sx={{ display: 'flex', minHeight: '100vh' }}>
//             <MainDrawer
//                 index={"clusters"}
//                 onStateChange={handleDrawerStateChange}
//             />
//             <Box
//                 component="main"
//                 sx={{
//                     flexGrow: 1,
//                     p: 0,
//                     transition: theme.transitions.create('margin', {
//                         easing: theme.transitions.easing.sharp,
//                         duration: theme.transitions.duration.enteringScreen,
//                     }),
//                     marginLeft: `${currentMarginLeft}vh`,
//                 }}
//             >
//                 <ClusterFormContent isEdit={copyIsEdit} />
//             </Box>
//         </Box>
//     );
// };

// export default ClusterForm;


// import React, { useState } from "react";
// import {
//     Box,
//     Button,
//     Container,
//     Grid,
//     TextField,
//     Typography,
//     MenuItem,
//     IconButton,
//     Breadcrumbs,
//     Link,
//     Divider,
//     Autocomplete,
//     useTheme,
// } from "@mui/material";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import { useNavigate, useParams } from "react-router-dom";
// import { Formik, Form, ErrorMessage, getIn } from "formik";
// import * as Yup from "yup";
// import CompulsoryFieldsLabel from "../../helpers/CompulsoryFields";
// import queryGraphql from "../../helpers/ApiInterface";
// import { useAuth } from "../../helpers/AuthProvider";
// import MiniBot from "../MiniBot";
// import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../MainDrawer"; // Adjust path if needed

// const gcpclusterTypeList = ["Autopilot", "Standard"];
// const awsclusterTypeList = ["Auto Mode", "Managed Node", "Fargate"]
// const cloudProviderList = [
//     { value: "gcp", label: "Google Cloud" },
//     { value: "aws", label: "Amazon Web Services" },
// ];

// const validationSchema = Yup.object().shape({
//     cloudProvider: Yup.string()
//         .required("This field is required")
//         .oneOf(["gcp", "aws"], "Invalid cloud provider"),
//     clusterName: Yup.string()
//         .required("This field is required")
//         .strict()
//         .lowercase("This field must be in lowercase")
//         .matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
//         .max(20, "This field must not exceed 20 characters"),
//     gcpProjectId: Yup.string() // This field will store GCP Project ID or AWS Account ID
//         .required("This field is required")
//         .strict()
//         .lowercase("This field must be in lowercase")
//         .matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
//         .max(30, "This field must not exceed 30 characters"),
//     region: Yup.string()
//         .required("This field is required")
//         .matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
//         .strict(),
//     gcpClusterViewers: Yup.array()
//         .of(
//             Yup.string()
//                 .required("This field is required")
//                 .matches(/^[a-zA-Z0-9._%+-]+@searce\.com$/, "Invalid domain")
//                 .strict()
//         )
//         .required("This field is required")
//         .min(1, "This field is required")
//         .strict(),
//     applicationRequesters: Yup.array()
//         .of(
//             Yup.string()
//                 .required("This field is required")
//                 .matches(/^[a-zA-Z0-9._%+-]+@searce\.com$/, "Invalid domain")
//                 .strict()
//         )
//         .required("This field is required")
//         .min(1, "This field is required")
//         .strict(),
//     costLabelPairs: Yup.array()
//         .of(
//             Yup.object({
//                 cost: Yup.string()
//                     .required("This field is required")
//                     .strict()
//                     .lowercase("This field must be in lowercase")
//                     .matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
//                     .max(20, "This field must not exceed 20 characters"),
//                 label: Yup.string()
//                     .required("This field is required")
//                     .strict()
//                     .lowercase("This field must be in lowercase")
//                     .matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
//                     .max(20, "This field must not exceed 20 characters"),
//             })
//         )
//         .required("This field is required"),
//     environment: Yup.string().required("This field is required").oneOf(["dev", "staging", "prod"]),
//     clusterSize: Yup.string().required("This field is required").oneOf(["small", "medium", "large"]),
//     clusterType: Yup.string().required("This field is required").oneOf(["Autopilot", "Standard", "Auto Mode", "Managed Node", "Fargate"]),
//     departmentValue: Yup.string()
//         .required("This field is required")
//         .strict()
//         .lowercase("This field must be in lowercase")
//         .matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
//         .max(30, "This field must not exceed 30 characters"),
//     costCenterValue: Yup.string()
//         .required("This field is required")
//         .strict()
//         .lowercase("This field must be in lowercase")
//         .matches(/^[a-zA-Z0-9-\s]*$/, "This field must be alphanumeric")
//         .max(20, "This field must not exceed 20 characters"),
// });

// const gcpregionList = [ // This list is currently GCP-centric. Update if AWS requires a different list.
//     "africa-south1", "asia-east1", "asia-east2", "asia-northeast1", "asia-northeast2",
//     "asia-northeast3", "asia-south1", "asia-south2", "asia-southeast1", "asia-southeast2",
//     "australia-southeast1", "australia-southeast2", "europe-central2", "europe-north1",
//     "europe-southwest1", "europe-west1", "europe-west10", "europe-west12", "europe-west2",
//     "europe-west3", "europe-west4", "europe-west6", "europe-west8", "europe-west9",
//     "me-central1", "me-central2", "me-west1", "northamerica-northeast1",
//     "northamerica-northeast2", "southamerica-east1", "southamerica-west1", "us-central1",
//     "us-east1", "us-east4", "us-east5", "us-south1", "us-west1", "us-west2",
//     "us-west3", "us-west4",
// ];
// const awsregionList = [ // This list is currently GCP-centric. Update if AWS requires a different list.
//    "us-east-2", "us-east-1" ,"us-west-1", "us-west-2", "af-south-1", "ap-east-1", "ap-south-2", "ap-southeast-3" ,"ap-southeast-5",
//     "ap-southeast-4" ,"ap-south-1" ,"ap-northeast-3", "ap-northeast-2" ,"ap-southeast-1" ,"ap-southeast-2" ,"ap-southeast-7",
// 	"ap-northeast-1", "ca-central-1", "ca-west-1", "eu-central-1", "eu-west-1" ,"eu-west-2" ,"eu-south-1" ,"eu-west-3" ,"eu-south-2",
//     "eu-north-1"," eu-central-2", "il-central-1", "mx-central-1", "me-south-1", "me-central-1", "sa-east-1", "us-gov-east-1", "us-gov-west-1"
// ];
// interface ClusterFormProps {
//     isEdit: boolean;
// }
// interface ClusterFormContentProps {
//     isEdit: boolean;
// }
// interface oneCluster {
//     id: string;
//     clusterName: string;
//     environment: string;
//     status: string;
//   }
// const ClusterFormContent: React.FC<ClusterFormContentProps> = ({ isEdit }) => {
//     const navigate = useNavigate();
//     const [users, setUsers] = useState([]);
//     const { user } = useAuth();
//     const isViewer = user.roles?.some((role: string) => role === "viewer") || false;
//     const [cluster, setCluster] = useState<any>({});
//     const [clusters, setClusters] = useState<any>({});
//     const { id } = useParams();
//     const [devDisable, setDevDisable] = useState<boolean>(false);
//     const [stageDisable, setStageDisable] = useState<boolean>(false);
//     const [prodDisable, setProdDisable] = useState<boolean>(false);
//     const [ClusterName, setClusterName] = useState<string>();
//     const [dummy, setDummy] = useState<any>();
    
//     React.useEffect(() => {
//         const fetchData = async () => {
//             try {
//                 const data = await queryGraphql(
//                     `
//                         query($clusterId: String!){
//                         cluster(id:$clusterId) {
//                         id,
//                         clusterName,
//                         cloudProvider, 
//                         clusterOwner{
//                         id,
//                         name,
//                         googleProfilePicture
//                         },
//                         gcpProjectId,
//                         environment,
//                         region,
//                         clusterSize,
//                         clusterType,
//                         gcpClusterViewers,
//                         applicationRequesters,
//                         costLabelPairs{
//                         cost,
//                         label
//                         },
//                         status,
//                         createdAt
//                         }
//                         }
// `,
//                     { clusterId: id }
//                 );
//                 if (data.cluster) {
//                     setCluster(data.cluster);
//                 }
//             } catch (error) {
//                 console.log(error);
//             }
//         };

//         if (isEdit && id) {
//             fetchData();
//         }
//     }, [id, isEdit]);

//     React.useEffect(() => {
//         const fetchData = async () => {
//             try {
//                 const data = await queryGraphql(`
//                         query Clusters {
//                             clusters {
//                                 id
//                                 clusterName
//                                 environment
//                                 status
//                             }
//                             }
//                             `);
//                 if (data.clusters) {
//                     setClusters(data.clusters.filter((e:any) => e.status !== "rejected"));
//                 }
//                 setDevDisable(false);
//                 setStageDisable(true);
//                 setProdDisable(true);
//                 clusters.forEach((item: oneCluster) => {
                    
//                     (ClusterName === item.clusterName && "dev" === item.environment )? setDevDisable(true): (ClusterName === item.clusterName && "staging" === item.environment )? setStageDisable(true) : (ClusterName === item.clusterName && "prod" === item.environment? setProdDisable(true) : setDummy(true) ) ;
                
//                     if(ClusterName === item.clusterName && "dev" === item.environment ){
//                         setDevDisable(true);
//                         setStageDisable(false);
//                         setProdDisable(false);
//                     }
//                     if(ClusterName === item.clusterName && "staging" === item.environment ){
//                         setDevDisable(true);
//                         setStageDisable(true);
//                         setProdDisable(false);
//                     }
//                     if(ClusterName === item.clusterName && "prod" === item.environment){
//                         setDevDisable(true);
//                         setStageDisable(true);
//                         setProdDisable(true);
//                     }
//                   });
//             } catch (error) {
//                 console.log(error);
//             }
//         };
//         fetchData();
//     }, [ClusterName, clusters]); // Added clusters to dependency array as it's used inside

//     React.useEffect(() => {
//         const fetchData = async () => {
//             try {
//                 const data = await queryGraphql(`
//                         query{
//                         users {
//                         id,
//                         email
//                         }
//                         }
// `);
//                 if (data.users) {
//                     setUsers(data.users);
//                 }
//             } catch (error) {
//                 console.log(error);
//             }
//         };

//         fetchData();
//     }, []);

//     return (
//         <Container
//             disableGutters
//             maxWidth={false}
//             sx={{ padding: "24px", bgcolor: "#fafafa", minHeight: "100vh" }}
//         >
//             <MiniBot/>
//             <Box sx={{ mb: 4 }}>
//                 <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
//                     <Link underline="hover" color="inherit" href="/clusters">
//                         Clusters
//                     </Link>
//                     <Typography color="text.primary">
//                         {isEdit ? "Edit Cluster Details" : "Add New Cluster"}
//                     </Typography>
//                 </Breadcrumbs>
//                 <Box display="flex" alignItems="center" mt={2}>
//                     <IconButton
//                         edge="start"
//                         aria-label="back"
//                         color="inherit"
//                         onClick={() => navigate("/clusters")}
//                     >
//                         <ArrowBackIosNewIcon fontSize="small" />
//                     </IconButton>
//                     <Typography fontFamily="Gilroy-Bold" fontSize="18px">
//                         {isEdit ? "Edit Cluster Details" : "Add New Cluster"}
//                     </Typography>
//                 </Box>
//             </Box>
//             <Grid container>
//                 <Grid item xs={1}></Grid>
//                 <Grid item xs={10}>
//                     {((isEdit && cluster.id) || !isEdit) && (
//                         <Formik
//                             initialValues={{
//                                 cloudProvider: isEdit ? (cluster.cloudProvider || "gcp") : "gcp",
//                                 clusterName: isEdit ? cluster.clusterName : "",
//                                 gcpProjectId: isEdit ? cluster.gcpProjectId : "",
//                                 region: isEdit ? cluster.region : "",
//                                 gcpClusterViewers: isEdit ? cluster.gcpClusterViewers : [],
//                                 applicationRequesters: isEdit ? cluster.applicationRequesters : [],
//                                 costLabelPairs: isEdit
//                                     ? cluster.costLabelPairs?.filter(
//                                             (e: any) => !["department", "cost-center", "cluster-owner"].includes(e.cost)
//                                       ) || []
//                                     : [],
//                                 environment: isEdit ? cluster.environment : "",
//                                 clusterSize: isEdit ? cluster.clusterSize : "",
//                                 clusterType: isEdit ? cluster.clusterType : "",
//                                 departmentValue: isEdit
//                                     ? cluster.costLabelPairs?.find((elem: any) => elem.cost === "department")?.label || ""
//                                     : "",
//                                 costCenterValue: isEdit
//                                     ? cluster.costLabelPairs?.find((elem: any) => elem.cost === "cost-center")?.label || ""
//                                     : "",
//                             }}
//                             validationSchema={validationSchema}
//                             onSubmit={async ({
//                                 cloudProvider,
//                                 applicationRequesters,
//                                 clusterType,
//                                 costLabelPairs,
//                                 environment,
//                                 gcpProjectId,
//                                 clusterName,
//                                 clusterSize,
//                                 gcpClusterViewers,
//                                 region,
//                                 departmentValue,
//                                 costCenterValue,
//                             }) => {
//                                 if (!isEdit) {
//                                     let keyValuePairs = [];
//                                     keyValuePairs.push(
//                                         {
//                                             cost: "cluster-owner",
//                                             label: (user.name as string).toLowerCase().replace(" ", "-"),
//                                         },
//                                         {
//                                             cost: "department",
//                                             label: departmentValue,
//                                         },
//                                         {
//                                             cost: "cost-center",
//                                             label: costCenterValue,
//                                         }
//                                     );
//                                     keyValuePairs.push(
//                                         ...costLabelPairs.map((e: any) => {
//                                             return { cost: e.cost, label: e.label };
//                                         })
//                                     );
//                                     const data = await queryGraphql(
//                                         `
//                                                 mutation CreateCluster($input: ClusterCreateInput) {
//                       createCluster(input: $input) {
//                         id
//                       }
//                     }
//                         `,
//                                         {
//                                             input: {
//                                                 cloudProvider,
//                                                 applicationRequesters,
//                                                 clusterType,
//                                                 costLabelPairs: keyValuePairs,
//                                                 environment,
//                                                 gcpProjectId,
//                                                 clusterName,
//                                                 clusterSize,
//                                                 gcpClusterViewers,
//                                                 region,
//                                             },
//                                         }
//                                     );

//                                     const info = await queryGraphql(`mutation Mutation($input: ResourceStatusCreateInput) {
//                                                                     createResourceStatus(input: $input) {
//                                                                         id
//                                                                     }
//                                                                     }`, {
//                                                                         input: {
//                                                                           currentStatus: "pending",
//                                                                           resourceEnvironment: environment,
//                                                                           resourceId: data.createCluster.id,
//                                                                           resourceName: clusterName,
//                                                                           resourceType: "cluster"
//                                                                         }
//                                                                       });


//                                     if (data.createCluster.id && info.createResourceStatus.id) {
//                                         navigate("/clusters");
//                                     }
//                                     return;
//                                 } else {
//                                     let keyValuePairs = [];
//                                     keyValuePairs.push(
//                                         {
//                                             cost: "cluster-owner",
//                                             label: (cluster.clusterOwner?.name || user.name as string).toLowerCase().replace(" ", "-"),
//                                         },
//                                         {
//                                             cost: "department",
//                                             label: departmentValue,
//                                         },
//                                         {
//                                             cost: "cost-center",
//                                             label: costCenterValue,
//                                         }
//                                     );
//                                     keyValuePairs.push(
//                                         ...costLabelPairs.map((e: any) => {
//                                             return { cost: e.cost, label: e.label };
//                                         })
//                                     );
//                                     const data = await queryGraphql(
//                                         `
//                                                mutation UpdateCluster($input: ClusterUpdateInput) {
//                                 updateCluster(input: $input) {
//                                   id
//                                 }
//                               }
//                         `,
//                                         {
//                                             input: {
//                                                 id: id,
//                                                 cloudProvider,
//                                                 applicationRequesters,
//                                                 clusterType,
//                                                 costLabelPairs: keyValuePairs,
//                                                 environment,
//                                                 gcpProjectId,
//                                                 clusterName,
//                                                 clusterSize,
//                                                 gcpClusterViewers,
//                                                 region,
//                                             },
//                                         }
//                                     );
//                                     const info = await queryGraphql(`mutation Mutation($input: ResourceStatusUpdateInput) {
//                                                                     updateResourceStatus(input: $input) {
//                                                                         id
//                                                                     }
//                                                                     }`, {
//                                                                         input: {
//                                                                           currentStatus: "pending",
//                                                                           resourceId: id ,
//                                                                           resourceName: clusterName
//                                                                         }
//                                                                       });

//                                     if (data.updateCluster.id && info.updateResourceStatus.id) {
//                                         navigate("/clusters");
//                                     }
//                                     return;
//                                 }
//                             }}
//                         >
//                             {({
//                                 handleChange,
//                                 handleBlur,
//                                 values,
//                                 setFieldValue,
//                                 errors,
//                                 touched,
//                                 isValid,
//                                 dirty,
//                             }) => {
//                                 return (                            
//                                     <Form>
//                                         <Box>
//                                             <Typography fontFamily="Gilroy-Medium" fontSize="16px" mb={4}>
//                                                 {isEdit
//                                                     ? "Make changes to below details and save"
//                                                     : "Fill the below details to add cluster"}
//                                             </Typography>
// 											<div
//                                                 style={{
//                                                     backgroundColor: "white",
//                                                     padding: "24px",
//                                                     borderRadius: "8px",
//                                                     boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
//                                                     marginBottom: "24px",
//                                                 }}
//                                             >
												
//                                                 <Typography
//                                                     fontFamily="Gilroy-Medium"
//                                                     fontSize="14px"
//                                                     style={{ marginBottom: "16px" }}
//                                                 >
//                                                     Cloud Provider Details
//                                                 </Typography>
// 												<Divider sx={{ marginBottom: "24px" }} />
// 												<Grid container spacing={4} style={{ marginBottom: "16px" }}>
//                                                     <Grid item xs={12} sm={12}> {/* Cloud Provider Full Width */}
//                                                         <CompulsoryFieldsLabel fieldName="Cloud Provider" />
//                                                         <TextField
//                                                             select
//                                                             placeholder="Select Cloud Provider"
//                                                             name="cloudProvider"
//                                                             value={values.cloudProvider}
//                                                             onChange={(e) => {
//                                                                 handleChange(e);
//                                                                 // Optionally reset fields that depend on cloud provider
//                                                                 // e.g., setFieldValue("region", "");
//                                                                 // e.g., setFieldValue("clusterType", "");
//                                                             }}
//                                                             onBlur={handleBlur}
//                                                             variant="filled"
//                                                             fullWidth
//                                                             hiddenLabel
//                                                             size="medium"
//                                                             sx={{
//                                                                 "& .MuiFilledInput-root": {
//                                                                     border:
//                                                                         errors["cloudProvider"] && touched["cloudProvider"]
//                                                                             ? "1px solid red"
//                                                                             : "1px solid #00000000",
//                                                                     "&.Mui-focused": {
//                                                                         backgroundColor: "white",
//                                                                         border: "1px solid black",
//                                                                     },
//                                                                 },
//                                                             }}
//                                                             InputProps={{
//                                                                 disableUnderline: true,
//                                                                 style: {
//                                                                     borderRadius: "8px",
//                                                                 },
//                                                             }}
//                                                         >
//                                                             {cloudProviderList.map((option) => (
//                                                                 <MenuItem key={option.value} value={option.value} onClick={() => setFieldValue("cloudProvider", option.value)}>
//                                                                     {option.label}
//                                                                 </MenuItem>
//                                                             ))}
//                                                         </TextField>
//                                                         <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                             <ErrorMessage name="cloudProvider" component="div" />
//                                                         </div>
//                                                     </Grid>
// 													</Grid>

// 											</div>
//                                             <div
//                                                 style={{
//                                                     backgroundColor: "white",
//                                                     padding: "24px",
//                                                     borderRadius: "8px",
//                                                     boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
//                                                     marginBottom: "24px",
//                                                 }}
//                                             >
//                                                 <Typography
//                                                     fontFamily="Gilroy-Medium"
//                                                     fontSize="14px"
//                                                     style={{ marginBottom: "16px" }}
//                                                 >
//                                                     Basic Information
//                                                 </Typography>
//                                                 <Divider sx={{ marginBottom: "24px" }} />
//                                                 <Grid container spacing={4} style={{ marginBottom: "16px" }}>
                                                    

//                                                     <Grid item xs={12} sm={6}>
//                                                         <CompulsoryFieldsLabel fieldName="Cluster Name" />
//                                                         <TextField
//                                                             sx={{
//                                                                 "& .MuiFilledInput-root": {
//                                                                     border:
//                                                                         errors["clusterName"] && touched["clusterName"]
//                                                                             ? "1px solid red"
//                                                                             : "1px solid #00000000",

//                                                                     "&.Mui-focused": {
//                                                                         backgroundColor: "white",
//                                                                         border: "1px solid black",
//                                                                     },
//                                                                 },
//                                                             }}
//                                                             variant="filled"
//                                                             fullWidth
//                                                             name="clusterName"
//                                                             value={values.clusterName}
//                                                             onChange= { (e) =>{
//                                                             handleChange(e);
//                                                             setClusterName(e.target.value);
//                                                             }}
//                                                             onBlur={handleBlur}
//                                                             placeholder="Enter name here"
//                                                             size="medium"
//                                                             hiddenLabel
//                                                             InputProps={{
//                                                                 disableUnderline: true,
//                                                                 style: {
//                                                                     borderRadius: "8px",
//                                                                 },
//                                                             }}
//                                                         />
//                                                         <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                             <ErrorMessage name="clusterName" component="div" />
//                                                         </div>
//                                                     </Grid>
//                                                     <Grid item xs={12} sm={6}>
//                                                         <CompulsoryFieldsLabel fieldName={values.cloudProvider === 'aws' ? "AWS Account ID" : "GCP Project ID"} />
//                                                         <TextField
//                                                             variant="filled"
//                                                             fullWidth
//                                                             name="gcpProjectId"
//                                                             value={values.gcpProjectId}
//                                                             onChange={handleChange}
//                                                             onBlur={handleBlur}
//                                                             placeholder={values.cloudProvider === 'aws' ? "Enter AWS Account ID" : "Enter GCP Project ID"}
//                                                             size="medium"
//                                                             sx={{
//                                                                 "& .MuiFilledInput-root": {
//                                                                     border:
//                                                                         errors["gcpProjectId"] && touched["gcpProjectId"]
//                                                                             ? "1px solid red"
//                                                                             : "1px solid #00000000",
//                                                                     "&.Mui-focused": {
//                                                                         backgroundColor: "white",
//                                                                         border: "1px solid black",
//                                                                     },
//                                                                 },
//                                                             }}
//                                                             hiddenLabel
//                                                             InputProps={{
//                                                                 disableUnderline: true,
//                                                                 style: {
//                                                                     borderRadius: "8px",
//                                                                 },
//                                                             }}
//                                                         />
//                                                         <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                             <ErrorMessage name="gcpProjectId" component="div" />
//                                                         </div>
//                                                     </Grid>
//                                                     <Grid item xs={12} sm={6}>
//                                                         <CompulsoryFieldsLabel fieldName="Cluster Type" />
//                                                         <TextField
//                                                             select
//                                                             placeholder="Select"
//                                                             name="clusterType"
//                                                             value={values.clusterType}
//                                                             onChange={handleChange}
//                                                             onBlur={handleBlur}
//                                                             variant="filled"
//                                                             fullWidth
//                                                             hiddenLabel
//                                                             size="medium"
//                                                             sx={{
//                                                                 "& .MuiFilledInput-root": {
//                                                                     border:
//                                                                         errors["clusterType"] && touched["clusterType"]
//                                                                             ? "1px solid red"
//                                                                             : "1px solid #00000000",
//                                                                     "&.Mui-focused": {
//                                                                         backgroundColor: "white",
//                                                                         border: "1px solid black",
//                                                                     },
//                                                                 },
//                                                             }}
//                                                             InputProps={{
//                                                                 disableUnderline: true,
//                                                                 style: {
//                                                                     borderRadius: "8px",
//                                                                 },
//                                                             }}
//                                                         >
//                                                             {values.cloudProvider === 'gcp' ? gcpclusterTypeList.map((e) => (
//                                                                 <MenuItem
//                                                                     key={e} // Added key prop
//                                                                     value={e}
//                                                                     onClick={() => setFieldValue("clusterType", e)}
//                                                                 >
//                                                                     {e}
//                                                                 </MenuItem>
//                                                             )) :
// 															awsclusterTypeList.map((e) => (
//                                                                 <MenuItem
//                                                                     key={e} // Added key prop
//                                                                     value={e}
//                                                                     onClick={() => setFieldValue("clusterType", e)}
//                                                                 >
//                                                                     {e}
//                                                                 </MenuItem> ))}
//                                                         </TextField>
//                                                         <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                             <ErrorMessage name="clusterType" component="div" />
//                                                         </div>
//                                                     </Grid>
//                                                     <Grid item xs={12} sm={6}>
//                                                         <CompulsoryFieldsLabel fieldName="Region" />
//                                                         <Autocomplete
//                                                              options={values.cloudProvider === 'gcp' ? gcpregionList : awsregionList}
//                                                             filterSelectedOptions
//                                                             value={values.region || null} // Ensure this is "" or null when cloudProvider changes
//                                                             onChange={(e, newVal, reason) => {
//                                                                 setFieldValue("region", newVal || "");
//                                                             }}
//                                                             renderInput={(params) => (
//                                                                 <TextField
//                                                                     {...params}
//                                                                     variant="filled"
//                                                                     fullWidth
//                                                                     placeholder="Select"
//                                                                     size="medium"
//                                                                     hiddenLabel
//                                                                     sx={{
//                                                                         "& .MuiFilledInput-root": {
//                                                                             border:
//                                                                                 errors["region"] && touched["region"]
//                                                                                     ? "1px solid red"
//                                                                                     : "1px solid #00000000",
//                                                                             "&.Mui-focused": {
//                                                                                 backgroundColor: "white",
//                                                                                 border: "1px solid black",
//                                                                             },
//                                                                         },
//                                                                     }}
//                                                                     InputProps={{
//                                                                         ...params.InputProps,
//                                                                         style: {
//                                                                             borderRadius: "8px",
//                                                                         },
//                                                                         disableUnderline: true,
//                                                                     }}
//                                                                 />
//                                                             )}
//                                                         />
//                                                         <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                             {getIn(errors, `region`)}
//                                                         </div>
//                                                     </Grid>
//                                                     <Grid item xs={12} sm={6}>
//                                                         <CompulsoryFieldsLabel fieldName="Environment" />
//                                                         <Box display="flex" gap={2} mt="14px">
//                                                             <Button
//                                                                 sx={{
//                                                                     borderRadius: 5,
//                                                                     borderColor: values.environment === "dev" ? "#000000" : "#666666",
//                                                                     color: values.environment === "dev" ? "#000000" : "#666666",
//                                                                     bgcolor: values.environment === "dev" ? "#E0E0E0" : "#FFFFFF",
//                                                                     textTransform: "none",
//                                                                 }}
//                                                                 variant="outlined"
//                                                                 onClick={() => setFieldValue("environment", "dev")}
//                                                                 disabled= {devDisable}
//                                                             >
//                                                                 dev
//                                                             </Button>
//                                                             <Button
//                                                                 sx={{
//                                                                     borderRadius: 5,
//                                                                     borderColor:
//                                                                         values.environment === "staging" ? "#000000" : "#666666",
//                                                                     color: values.environment === "staging" ? "#000000" : "#666666",
//                                                                     bgcolor: values.environment === "staging" ? "#E0E0E0" : "#FFFFFF",
//                                                                     textTransform: "none",
//                                                                 }}
//                                                                 variant="outlined"
//                                                                 onClick={() => setFieldValue("environment", "staging")}
//                                                                 disabled={stageDisable}
//                                                             >
//                                                                 staging
//                                                             </Button>
//                                                             <Button
//                                                                 sx={{
//                                                                     borderRadius: 5,
//                                                                     borderColor: values.environment === "prod" ? "#000000" : "#666666",
//                                                                     color: values.environment === "prod" ? "#000000" : "#666666",
//                                                                     bgcolor: values.environment === "prod" ? "#E0E0E0" : "#FFFFFF",
//                                                                     textTransform: "none",
//                                                                 }}
//                                                                 variant="outlined"
//                                                                 onClick={() => setFieldValue("environment", "prod")}
//                                                                 disabled={prodDisable}
//                                                             >
//                                                                 prod
//                                                             </Button>
//                                                         </Box>
//                                                         <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                             <ErrorMessage name="environment" component="div" />
//                                                         </div>
//                                                     </Grid>
//                                                     <Grid item xs={12} sm={6}>
//                                                         <CompulsoryFieldsLabel fieldName="Cluster Size" />
//                                                         <Box display="flex" gap={2} mt="14px">
//                                                             <Button
//                                                                 sx={{
//                                                                     borderRadius: 5,
//                                                                     borderColor:
//                                                                         values.clusterSize === "small" ? "#000000" : "#666666",
//                                                                     color: values.clusterSize === "small" ? "#000000" : "#666666",
//                                                                     bgcolor: values.clusterSize === "small" ? "#E0E0E0" : "#FFFFFF",
//                                                                     textTransform: "none",
//                                                                 }}
//                                                                 variant="outlined"
//                                                                 onClick={() => setFieldValue("clusterSize", "small")}
//                                                             >
//                                                                 small
//                                                             </Button>
//                                                             <Button
//                                                                 sx={{
//                                                                     borderRadius: 5,
//                                                                     borderColor:
//                                                                         values.clusterSize === "medium" ? "#000000" : "#666666",
//                                                                     color: values.clusterSize === "medium" ? "#000000" : "#666666",
//                                                                     bgcolor: values.clusterSize === "medium" ? "#E0E0E0" : "#FFFFFF",
//                                                                     textTransform: "none",
//                                                                 }}
//                                                                 variant="outlined"
//                                                                 onClick={() => setFieldValue("clusterSize", "medium")}
//                                                             >
//                                                                 medium
//                                                             </Button>
//                                                             <Button
//                                                                 sx={{
//                                                                     borderRadius: 5,
//                                                                     borderColor:
//                                                                         values.clusterSize === "large" ? "#000000" : "#666666",
//                                                                     color: values.clusterSize === "large" ? "#000000" : "#666666",
//                                                                     bgcolor: values.clusterSize === "large" ? "#E0E0E0" : "#FFFFFF",
//                                                                     textTransform: "none",
//                                                                 }}
//                                                                 variant="outlined"
//                                                                 onClick={() => setFieldValue("clusterSize", "large")}
//                                                             >
//                                                                 large
//                                                             </Button>
//                                                         </Box>
//                                                         <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                             <ErrorMessage name="clusterSize" component="div" />
//                                                         </div>
//                                                     </Grid>
//                                                 </Grid>
//                                             </div>

//                                             <div
//                                                 style={{
//                                                     backgroundColor: "white",
//                                                     padding: "24px",
//                                                     borderRadius: "8px",
//                                                     boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
//                                                     marginBottom: "24px",
//                                                 }}
//                                             >
//                                                 <Typography
//                                                     fontFamily="Gilroy-Medium"
//                                                     fontSize="14px"
//                                                     style={{ marginBottom: "16px" }}
//                                                 >
//                                                     Cluster Viewers and Application Owners
//                                                 </Typography>
//                                                 <Divider sx={{ marginBottom: "24px" }} />
//                                                 <Grid container spacing={4} style={{ marginBottom: "16px" }}>
//                                                     <Grid item xs={12} sm={6}>
//                                                         <CompulsoryFieldsLabel fieldName="Cluster Viewers" />
//                                                         <Autocomplete
//                                                             multiple
//                                                             options={users.map((e: any) => e.email)}
//                                                             filterSelectedOptions
//                                                             ChipProps={{ size: "small" }}
//                                                             freeSolo
//                                                             value={values.gcpClusterViewers} // Controlled component
//                                                             onChange={(e, newVal, reason) => {
//                                                                 setFieldValue("gcpClusterViewers", newVal);
//                                                             }}
//                                                             renderInput={(params) => (
//                                                                 <TextField
//                                                                     {...params}
//                                                                     variant="filled"
//                                                                     fullWidth
//                                                                     placeholder="e.g. abc121@searce.com"
//                                                                     size="small"
//                                                                     hiddenLabel
//                                                                     InputProps={{
//                                                                         ...params.InputProps,
//                                                                         style: {
//                                                                             borderRadius: "8px",
//                                                                         },
//                                                                         disableUnderline: true,
//                                                                     }}
//                                                                 />
//                                                             )}
//                                                         />
//                                                         <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                             {typeof getIn(errors, `gcpClusterViewers`) === "string"
//                                                                 ? getIn(errors, `gcpClusterViewers`)
//                                                                 : (getIn(errors, `gcpClusterViewers`) as string[])?.filter(
//                                                                         (e) => e != null
//                                                                   )?.[0]}
//                                                         </div>
//                                                     </Grid>
//                                                     <Grid item xs={12} sm={6}>
//                                                         <CompulsoryFieldsLabel fieldName="Application Owners" />
//                                                         <Autocomplete
//                                                             multiple
//                                                             options={users.map((e: any) => e.email)}
//                                                             filterSelectedOptions
//                                                             ChipProps={{ size: "small" }}
//                                                             freeSolo
//                                                             value={values.applicationRequesters} // Controlled component
//                                                             onChange={(e, newVal, reason) => {
//                                                                 setFieldValue("applicationRequesters", newVal);
//                                                             }}
//                                                             renderInput={(params) => (
//                                                                 <TextField
//                                                                     {...params}
//                                                                     variant="filled"
//                                                                     fullWidth
//                                                                     placeholder="e.g. abc121@searce.com"
//                                                                     size="small"
//                                                                     hiddenLabel
//                                                                     InputProps={{
//                                                                         ...params.InputProps,
//                                                                         style: {
//                                                                             borderRadius: "8px",
//                                                                         },
//                                                                         disableUnderline: true,
//                                                                     }}
//                                                                 />
//                                                             )}
//                                                         />
//                                                         <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                             {typeof getIn(errors, `applicationRequesters`) === "string"
//                                                                 ? getIn(errors, `applicationRequesters`)
//                                                                 : (getIn(errors, `applicationRequesters`) as string[])?.filter(
//                                                                         (e) => e != null
//                                                                   )?.[0]}
//                                                         </div>
//                                                     </Grid>
//                                                 </Grid>
//                                             </div>

//                                             <div
//                                                 style={{
//                                                     backgroundColor: "white",
//                                                     padding: "24px",
//                                                     borderRadius: "8px",
//                                                     boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
//                                                     marginBottom: "24px",
//                                                 }}
//                                             >
//                                                 <Typography
//                                                     fontFamily="Gilroy-Medium"
//                                                     fontSize="14px"
//                                                     style={{ marginBottom: "16px" }}
//                                                 >
//                                                     Cluster Tags / Labels
//                                                 </Typography>
//                                                 <Divider sx={{ marginBottom: "24px" }} />
//                                                 <Grid container spacing={4} style={{ marginBottom: "16px" }}>
//                                                     <Grid item xs={12} sm={6}>
//                                                         <Typography
//                                                             gutterBottom
//                                                             fontFamily="Gilroy-Regular"
//                                                             fontSize="14px"
//                                                             ml="4px"
//                                                         >
//                                                             Key
//                                                         </Typography>
//                                                     </Grid>
//                                                     <Grid item xs={12} sm={6}>
//                                                         <Typography
//                                                             gutterBottom
//                                                             fontFamily="Gilroy-Regular"
//                                                             fontSize="14px"
//                                                             ml="4px"
//                                                         >
//                                                             Value
//                                                         </Typography>
//                                                     </Grid>
//                                                 </Grid>
//                                                 <Grid container spacing={4}>
//                                                     <Grid item xs={12} sm={6}>
//                                                         <TextField
//                                                             fullWidth
//                                                             placeholder="Enter key"
//                                                             size="medium"
//                                                             hiddenLabel
//                                                             disabled={true}
//                                                             value="cluster-owner"
//                                                             variant="filled"
//                                                             sx={{ marginBottom: "0px" }}
//                                                             InputProps={{
//                                                                 disableUnderline: true,
//                                                                 style: {
//                                                                     borderRadius: "8px",
//                                                                 },
//                                                             }}
//                                                         />
//                                                     </Grid>
//                                                     <Grid item xs={12} sm={6}>
//                                                         <Box display="flex" alignItems="center" justifyContent="space-between">
//                                                             <TextField
//                                                                 fullWidth
//                                                                 placeholder="Enter value"
//                                                                 size="medium"
//                                                                 value={(isEdit && cluster.clusterOwner?.name
//                                                                     ? (cluster.clusterOwner.name as string)
//                                                                     : (user.name as string)
//                                                                 )
//                                                                     .toLowerCase()
//                                                                     .replace(" ", "-")}
//                                                                 hiddenLabel
//                                                                 sx={{ marginBottom: "0px" }}
//                                                                 variant="filled"
//                                                                 disabled={true}
//                                                                 InputProps={{
//                                                                     disableUnderline: true,
//                                                                     style: {
//                                                                         borderRadius: "8px",
//                                                                     },
//                                                                 }}
//                                                             />
//                                                         </Box>
//                                                     </Grid>
//                                                     <Grid item xs={12} sm={6}>
//                                                         <TextField
//                                                             fullWidth
//                                                             placeholder="Enter key"
//                                                             size="medium"
//                                                             hiddenLabel
//                                                             disabled={true}
//                                                             value="department"
//                                                             variant="filled"
//                                                             sx={{ marginBottom: "0px" }}
//                                                             InputProps={{
//                                                                 disableUnderline: true,
//                                                                 style: {
//                                                                     borderRadius: "8px",
//                                                                 },
//                                                             }}
//                                                         />
//                                                     </Grid>
//                                                     <Grid item xs={12} sm={6}>
//                                                         <Box display="flex" alignItems="center" justifyContent="space-between">
//                                                             <TextField
//                                                                 fullWidth
//                                                                 placeholder="Enter value"
//                                                                 size="medium"
//                                                                 value={values.departmentValue}
//                                                                 hiddenLabel
//                                                                 variant="filled"
//                                                                 name="departmentValue"
//                                                                 sx={{
//                                                                     "& .MuiFilledInput-root": {
//                                                                         border:
//                                                                             errors["departmentValue"] && touched["departmentValue"]
//                                                                                 ? "1px solid red"
//                                                                                 : "1px solid #00000000",
//                                                                         "&.Mui-focused": {
//                                                                             backgroundColor: "white",
//                                                                             border: "1px solid black",
//                                                                         },
//                                                                     },
//                                                                     marginBottom: "0px",
//                                                                 }}
//                                                                 onChange={handleChange}
//                                                                 onBlur={handleBlur}
//                                                                 InputProps={{
//                                                                     disableUnderline: true,
//                                                                     style: {
//                                                                         borderRadius: "8px",
//                                                                     },
//                                                                 }}
//                                                             />
//                                                         </Box>
//                                                         <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                             <ErrorMessage name="departmentValue" component="div" />
//                                                         </div>
//                                                     </Grid>
//                                                     <Grid item xs={12} sm={6}>
//                                                         <TextField
//                                                             fullWidth
//                                                             placeholder="Enter key"
//                                                             size="medium"
//                                                             hiddenLabel
//                                                             disabled={true}
//                                                             value="cost-center"
//                                                             variant="filled"
//                                                             sx={{ marginBottom: "0px" }}
//                                                             InputProps={{
//                                                                 disableUnderline: true,
//                                                                 style: {
//                                                                     borderRadius: "8px",
//                                                                 },
//                                                             }}
//                                                         />
//                                                     </Grid>
//                                                     <Grid item xs={12} sm={6}>
//                                                         <Box display="flex" alignItems="center" justifyContent="space-between">
//                                                             <TextField
//                                                                 fullWidth
//                                                                 placeholder="Enter value"
//                                                                 size="medium"
//                                                                 value={values.costCenterValue}
//                                                                 hiddenLabel
//                                                                 variant="filled"
//                                                                 name="costCenterValue"
//                                                                 sx={{
//                                                                     "& .MuiFilledInput-root": {
//                                                                         border:
//                                                                             errors["costCenterValue"] && touched["costCenterValue"]
//                                                                                 ? "1px solid red"
//                                                                                 : "1px solid #00000000",
//                                                                         "&.Mui-focused": {
//                                                                             backgroundColor: "white",
//                                                                             border: "1px solid black",
//                                                                         },
//                                                                     },
//                                                                     marginBottom: "0px",
//                                                                 }}
//                                                                 onChange={handleChange}
//                                                                 onBlur={handleBlur}
//                                                                 InputProps={{
//                                                                     disableUnderline: true,
//                                                                     style: {
//                                                                         borderRadius: "8px",
//                                                                     },
//                                                                 }}
//                                                             />
//                                                         </Box>
//                                                         <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                             <ErrorMessage name="costCenterValue" component="div" />
//                                                         </div>
//                                                     </Grid>
//                                                 </Grid>

//                                                 <Grid container spacing={4} mt="8px">
//                                                     {values.costLabelPairs.map((e: any, index: number) => (
//                                                         <React.Fragment key={e.id || index}> {/* Added key to fragment */}
//                                                             <Grid item xs={12} sm={6}>
//                                                                 <TextField
//                                                                     fullWidth
//                                                                     placeholder="Enter key"
//                                                                     size="medium"
//                                                                     hiddenLabel
//                                                                     value={e.cost}
//                                                                     sx={{
//                                                                         "& .MuiFilledInput-root": {
//                                                                             border: getIn(errors, `costLabelPairs[${index}].cost`) && getIn(touched, `costLabelPairs[${index}].cost`) ? "1px solid red" : "1px solid #00000000", // Error indication
//                                                                             "&.Mui-focused": {
//                                                                                 backgroundColor: "white",
//                                                                                 border: "1px solid black",
//                                                                             },
//                                                                         },
//                                                                         marginBottom: "0px",
//                                                                     }}
//                                                                     variant="filled"
//                                                                     onChange={(event) => {
//                                                                         const newCostLabelPairs = values.costLabelPairs.map((pair: any, pairIndex: number) => {
//                                                                             if (index === pairIndex) { // Compare by index, assuming 'id' might not always be there for new pairs
//                                                                                 return { ...pair, cost: event.target.value || "" };
//                                                                             }
//                                                                             return pair;
//                                                                         });
//                                                                         setFieldValue("costLabelPairs", newCostLabelPairs);
//                                                                     }}
//                                                                     onBlur={handleBlur} // Add onBlur for validation trigger
//                                                                     name={`costLabelPairs[${index}].cost`} // Add name for Formik
//                                                                     InputProps={{
//                                                                         disableUnderline: true,
//                                                                         style: {
//                                                                             borderRadius: "8px",
//                                                                         },
//                                                                     }}
//                                                                 />
//                                                                 <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                                     <ErrorMessage name={`costLabelPairs[${index}].cost`} />
//                                                                 </div>
//                                                             </Grid>
//                                                             <Grid item xs={12} sm={6}>
//                                                                 <Box
//                                                                     display="flex"
//                                                                     alignItems="center"
//                                                                     justifyContent="space-between"
//                                                                 >
//                                                                     <TextField
//                                                                         fullWidth
//                                                                         placeholder="Enter value"
//                                                                         size="medium"
//                                                                         sx={{
//                                                                             "& .MuiFilledInput-root": {
//                                                                                 border: getIn(errors, `costLabelPairs[${index}].label`) && getIn(touched, `costLabelPairs[${index}].label`) ? "1px solid red" : "1px solid #00000000", // Error indication
//                                                                                 "&.Mui-focused": {
//                                                                                     backgroundColor: "white",
//                                                                                     border: "1px solid black",
//                                                                                 },
//                                                                             },
//                                                                             marginBottom: "0px",
//                                                                         }}
//                                                                         value={e.label}
//                                                                         hiddenLabel
//                                                                         variant="filled"
//                                                                         onChange={(event) => {
//                                                                             const newCostLabelPairs = values.costLabelPairs.map((pair: any, pairIndex: number) => {
//                                                                                 if (index === pairIndex) { // Compare by index
//                                                                                     return { ...pair, label: event.target.value || "" };
//                                                                                 }
//                                                                                 return pair;
//                                                                             });
//                                                                             setFieldValue("costLabelPairs", newCostLabelPairs);
//                                                                         }}
//                                                                         onBlur={handleBlur} // Add onBlur for validation trigger
//                                                                         name={`costLabelPairs[${index}].label`} // Add name for Formik
//                                                                         InputProps={{
//                                                                             disableUnderline: true,
//                                                                             style: {
//                                                                                 borderRadius: "8px",
//                                                                             },
//                                                                         }}
//                                                                     />

//                                                                     <Button
//                                                                         sx={{ marginLeft: "8px" }}
//                                                                         onClick={() => {
//                                                                             let clpArr = values.costLabelPairs.filter((pair: any, pairIndex: number) => {
//                                                                                 return index !== pairIndex; // Compare by index
//                                                                             });
//                                                                             setFieldValue("costLabelPairs", clpArr);
//                                                                         }}
//                                                                     >
//                                                                         <img alt="close-icon" src="/assets/close-icon.svg" />
//                                                                     </Button>
//                                                                 </Box>
//                                                                 <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}>
//                                                                     <ErrorMessage name={`costLabelPairs[${index}].label`} />
//                                                                 </div>
//                                                             </Grid>
//                                                         </React.Fragment>
//                                                     ))}
//                                                 </Grid>

//                                                 <Button
//                                                     variant="text"
//                                                     sx={{ color: "#000000", textTransform: "none", mt: "16px" }}
//                                                     onClick={() =>
//                                                         setFieldValue("costLabelPairs", [
//                                                             ...values.costLabelPairs,
//                                                             { cost: "", label: "", id: crypto.randomUUID() }, // id ensures unique key for new pairs
//                                                         ])
//                                                     }
//                                                 >
//                                                     + Add Key Value Pair
//                                                 </Button>
//                                             </div>
//                                         </Box>

//                                         <Box display="flex" justifyContent="flex-end" gap={2}>
//                                             <Button
//                                                 sx={{
//                                                     color: "black",
//                                                     borderColor: "black",
//                                                     fontWeight: "bold",
//                                                     textTransform: "none",
//                                                     px: 4,
//                                                 }}
//                                                 variant="outlined"
//                                                 size="large"
//                                                 onClick={() => navigate("/clusters")}
//                                             >
//                                                 Cancel
//                                             </Button>
//                                             <Button
//                                                 sx={{
//                                                     backgroundColor: "black",
//                                                     color: "white",
//                                                     fontWeight: "bold",
//                                                     "&:hover": {
//                                                         backgroundColor: "white",
//                                                         color: "black",
//                                                         borderColor: "black",
//                                                     },
//                                                     textTransform: "none",
//                                                     px: 4,
//                                                 }}
//                                                 variant="contained"
//                                                 size="large"
//                                                 type="submit"
//                                                 disabled={!(isValid && dirty) || isViewer}
//                                             >
//                                                 {isEdit ? "Save Changes" : "Add New Cluster"}
//                                             </Button>
//                                         </Box>
//                                     </Form>
//                                 );
//                             }}
//                         </Formik>
//                     )}
//                 </Grid>
//                 <Grid item xs={1}></Grid>
//             </Grid>
//         </Container>
//     );
// };

// const ClusterForm: React.FC<ClusterFormProps> = ({ isEdit }) => {
//     const theme = useTheme();

//     const getInitialDrawerState = () => {
//         if (typeof window !== 'undefined') {
//             const storedState = localStorage.getItem('isFaradayDrawerOpen');
//             return storedState !== null ? JSON.parse(storedState) : true;
//         }
//         return true;
//     };
//     const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);

//     const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
//         setIsDrawerActuallyOpen(isOpenFromDrawer);
//     };

//     const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;
//     const copyIsEdit = isEdit; // Renaming to avoid conflict if isEdit was a prop of ClusterForm itself directly
    
//     return (
//         <Box sx={{ display: 'flex', minHeight: '100vh' }}>
//             <MainDrawer
//                 index={"clusters"} 
//                 onStateChange={handleDrawerStateChange}
//             />
//             <Box
//                 component="main"
//                 sx={{
//                     flexGrow: 1,
//                     p: 0,
//                     transition: theme.transitions.create('margin', {
//                         easing: theme.transitions.easing.sharp,
//                         duration: theme.transitions.duration.enteringScreen,
//                     }),
//                     marginLeft: `${currentMarginLeft}vh`, 
//                 }}
//             >
//                 <ClusterFormContent isEdit={copyIsEdit} />
//             </Box>
//         </Box>
//     );
// };

// export default ClusterForm;